import { create } from 'zustand';
import { toast } from 'sonner';
import { NODE_TYPES, AI_NODE_TYPES, generateId, getNodeTypes } from '../components/visual-flow-builder/constants';
import { reIdNodeTree } from '../utils/reIdNodeTree';
import { addIdAfter } from '../components/visual-flow-builder/insertionContext';
import { normalizeLegacyResource } from '../utils/normalizeLegacyResource';
import { getRecordComponentResources } from '@/utils/recordComponentResources';
import { tagBrokenFlowRefs as _tagBrokenFlowRefs, healBrokenFlowRefs as _healBrokenFlowRefs } from '@/utils/outlineReferences';
// Provenance (docs/provenance-spec.md): records that the flow was BUILT, node
// by node, rather than pasted. A no-op unless a work session is open.
import { logWork } from '../services/workLog';
import { renameInputPathsInNodes } from '../utils/renameInputPaths';

// What KIND of resource this is, for the work log. The builder's own field is
// `resourceType`; older surfaces pass `type`. Reading only `type` recorded null
// for every variable a learner declared — an event that proves nothing.
const resourceKind = (resource) => resource?.resourceType || resource?.type || null;

// Helper to get node config from either programming or AI node types
const getNodeConfig = (type) => {
  return NODE_TYPES[type] || AI_NODE_TYPES[type] || { label: type };
};

// Helper to find node by ID at any depth
const findNodeById = (nodes, nodeId) => {
  for (const node of nodes) {
    if (node.id === nodeId) return node;
    if ((node.type === 'decision' || node.type === 'switch') && node.outcomes) {
      for (const outcome of node.outcomes) {
        const found = findNodeById(outcome.nodes || [], nodeId);
        if (found) return found;
      }
    }
    if (node.type === 'loop') {
      if (node.loopBody) {
        const found = findNodeById(node.loopBody, nodeId);
        if (found) return found;
      }
      if (node.afterLast) {
        const found = findNodeById(node.afterLast, nodeId);
        if (found) return found;
      }
    }
    if (node.type === 'try_catch' && node.branches) {
      for (const branch of node.branches) {
        const found = findNodeById(branch.nodes || [], nodeId);
        if (found) return found;
      }
    }
  }
  return null;
};

// Helper to find and update node at any depth
const findAndUpdateNode = (nodes, nodeId, updates) => {
  return nodes.map(node => {
    if (node.id === nodeId) return { ...node, ...updates };
    const updatedNode = { ...node };
    if ((node.type === 'decision' || node.type === 'switch') && node.outcomes) {
      updatedNode.outcomes = node.outcomes.map(outcome => ({
        ...outcome,
        nodes: findAndUpdateNode(outcome.nodes || [], nodeId, updates)
      }));
    }
    if (node.type === 'loop') {
      if (node.loopBody) updatedNode.loopBody = findAndUpdateNode(node.loopBody, nodeId, updates);
      if (node.afterLast) updatedNode.afterLast = findAndUpdateNode(node.afterLast, nodeId, updates);
    }
    if (node.type === 'try_catch' && node.branches) {
      updatedNode.branches = node.branches.map(branch => ({
        ...branch,
        nodes: findAndUpdateNode(branch.nodes || [], nodeId, updates)
      }));
    }
    return updatedNode;
  });
};

// Helper to find and delete node at any depth
const findAndDeleteNode = (nodes, nodeId) => {
  const result = [];
  for (const node of nodes) {
    if (node.id === nodeId) continue;
    const updatedNode = { ...node };
    if ((node.type === 'decision' || node.type === 'switch') && node.outcomes) {
      updatedNode.outcomes = node.outcomes.map(outcome => ({
        ...outcome,
        nodes: findAndDeleteNode(outcome.nodes || [], nodeId)
      }));
    }
    if (node.type === 'loop') {
      if (node.loopBody) updatedNode.loopBody = findAndDeleteNode(node.loopBody, nodeId);
      if (node.afterLast) updatedNode.afterLast = findAndDeleteNode(node.afterLast, nodeId);
    }
    if (node.type === 'try_catch' && node.branches) {
      updatedNode.branches = node.branches.map(branch => ({
        ...branch,
        nodes: findAndDeleteNode(branch.nodes || [], nodeId)
      }));
    }
    result.push(updatedNode);
  }
  return result;
};

// Count all nodes recursively
const countNodes = (nodes) => {
  let count = 0;
  for (const node of nodes) {
    if (node.type !== 'start' && node.type !== 'end') count++;
    if ((node.type === 'decision' || node.type === 'switch') && node.outcomes) {
      for (const outcome of node.outcomes) count += countNodes(outcome.nodes || []);
    }
    if (node.type === 'loop') {
      if (node.loopBody) count += countNodes(node.loopBody);
      if (node.afterLast) count += countNodes(node.afterLast);
    }
    if (node.type === 'try_catch' && node.branches) {
      for (const branch of node.branches) count += countNodes(branch.nodes || []);
    }
  }
  return count;
};

// ADO #223 — valid flow types (Java-centric but applies to all tracks).
// Non-Java tracks only ever use 'main' / 'static'; other values are meaningful
// in Java (and later Kotlin / C++ / Apex).
// ADO #223/237/242/243/244 — every flow-type the schema understands. Anything
// not in this set is treated as a legacy flow and re-derived from is_entry +
// is_static; that path used to silently rewrite saved 'contract' flows to
// 'static' on load.
const VALID_FLOW_TYPES = [
  'main', 'static', 'instance',
  'constructor', 'compactConstructor', 'staticInitializer', 'destructor',
  'staticMethod', 'defaultMethod', 'classMethod', 'dunderMethod',
  'propertyGetter', 'propertySetter', 'propertyDeleter',
  'operator', 'function', 'accessor',
  'contract',
];
const VALID_ACCESS = ['public', 'private', 'protected', 'package'];

/**
 * ADO #223 — Migrate a legacy flow (pre-flowType era) to the new schema.
 * Backwards compatible: legacy flows with only `isEntry` / `isStatic` get
 * mapped to the corresponding `flowType`. Flows that already have `flowType`
 * are left alone. `accessSpecifier` defaults to 'public' when missing.
 *
 * isEntry=true                        → flowType='main'
 * isEntry=false + isStatic !== false  → flowType='static'  (legacy default)
 * isEntry=false + isStatic === false  → flowType='instance'
 */
const migrateFlowSchema = (flow) => {
  if (!flow || typeof flow !== 'object') return flow;
  const migrated = { ...flow };

  // flowType migration — only set if missing OR not in the valid set
  if (!VALID_FLOW_TYPES.includes(migrated.flowType)) {
    if (migrated.isEntry) {
      migrated.flowType = 'main';
    } else if (migrated.isStatic === false) {
      migrated.flowType = 'instance';
    } else {
      // Legacy default: non-entry flows were treated as static
      migrated.flowType = 'static';
    }
  }

  // accessSpecifier default
  if (!VALID_ACCESS.includes(migrated.accessSpecifier)) {
    migrated.accessSpecifier = 'public';
  }

  return migrated;
};

// Create initial flow with its own history and clipboard
// ADO #582 — the legacy dialog edits with `type` ('ARRAY', 'VARIABLE', ...)
// and never sends a resourceType, so re-derive it when its type changes.
const withLegacyTypeUpdate = (r, updates) => {
  const merged = { ...r, ...updates };
  if (updates && updates.type && !updates.resourceType && /^[A-Z_0-9]+$/.test(String(updates.type))) {
    return normalizeLegacyResource({ ...merged, resourceType: undefined });
  }
  return merged;
};

const createInitialFlow = (name = 'main', isEntry = true) => {
  const initialNodes = [
    { id: `start-${Date.now()}`, type: 'start', label: 'Start' },
    { id: `end-${Date.now()}`, type: 'end', label: 'End' }
  ];
  const initialResources = [];

  return {
    id: `flow-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
    name,
    // Legacy fields — kept for backwards compatibility; new code should read flowType
    isEntry,
    // ADO #223 — canonical flow-type + access-specifier fields
    flowType: isEntry ? 'main' : 'static',
    accessSpecifier: 'public',
    parameters: [],
    returnType: 'void',
    nodes: initialNodes,
    resources: initialResources,
    // Per-flow history (like Google Sheets)
    history: [JSON.stringify({ nodes: initialNodes, resources: initialResources })],
    historyIndex: 0,
    skipHistoryPush: false,
    // Per-flow clipboard (like Google Sheets)
    clipboard: null,
    clipboardAction: null, // 'cut' | 'copy'
    cutNodeId: null // Track which node is being cut for visual feedback
  };
};

// ADO #242 — create the Contract Flow tab for a session.
// Contract Flow is always tab position 0. It has Start + End nodes like
// any flow but uses the contract node palette (Configuration, Constructor,
// Method). Its flowType is 'contract' which the UI uses to swap palettes
// and prevent deletion.
// ADO #267 follow-up — predicate identifying orphan phantom main flows.
// `createInitialFlow` produces an entry main with no fileName / sessionId
// at module init. If saved before addFile bootstrap remaps it, it persists
// to the DB without a fileName, then sits alongside the real bound main
// after a 2nd file is added. Same-named bound-twin requirement keeps
// fresh single-file projects (no files row yet) intact — without a twin
// the unbound main is the legitimate sole main, not an orphan.
const _isOrphanPhantomFlow = (flow, allFlows) =>
  flow.isEntry
  && !flow.fileName
  && !flow.sessionId
  && flow.flowType !== 'contract'
  && allFlows.some((other) =>
    other !== flow && other.name === flow.name && !!other.fileName
  );

// ADO #580 — the entry flow (main) keeps its identity.
// A project lost its entry: the flow made with the Contract became `indexOf`
// and the learner's later `main` was saved as an ordinary void function, so
// Run built indexOf's body as main plus a second `void main`. The entry can't
// be renamed into another function, and a flow named main with no entry in
// the project becomes the entry.
const _flowHasContent = (f) => (f?.nodes || []).some((n) => n.type !== 'start' && n.type !== 'end');
export const isEntryFlow = (f) => !!f && f.flowType !== 'contract' && (f.isEntry === true || f.flowType === 'main');
const _isCFlow = (f, projectLanguage) =>
  /\.c$/i.test(f?.fileName || '') || (!f?.fileName && projectLanguage === 'c');

export const entryRenameMessage = (entryName, wanted) =>
  `${entryName || 'main'} is where the program starts; add a new function${wanted ? ` for ${wanted}` : ''} instead.`;

// A flow that became the entry only by being NAMED main, and is unchanged
// since, is still on its way somewhere: typing `mainMenu` passes through
// `main`. Renaming it on lets it go back to being a function.
const _isPassingThroughMain = (f) =>
  !!f?._entryByName && f._entryByName.nodes === JSON.stringify(f.nodes || []);

/** The refusal message when `flow` is the entry and may not be called `newName`; else null. */
export const entryRenameRefusal = (flow, newName) => {
  if (!isEntryFlow(flow)) return null;
  if (typeof newName !== 'string') return null;
  const wanted = newName.trim();
  if (wanted === flow.name || wanted === 'main') return null;
  if (_isPassingThroughMain(flow)) return null;
  return entryRenameMessage(flow.name, wanted);
};

const _refuse = (message) => toast.error(message, { id: 'entry-rename-refused' });

// Rewrite every Contract function_decl implemented by `implFlowId`.
const _mapDeclsOf = (flows, implFlowId, patch, touched) => flows.map((f) => {
  if (f.flowType !== 'contract') return f;
  let changed = false;
  const nodes = (f.nodes || []).map((n) => {
    if (n.type !== 'function_decl' || n._implFlowId !== implFlowId) return n;
    const next = patch(n);
    if (next !== n) changed = true;
    return next;
  });
  if (!changed) return f;
  if (touched) touched.add(f.id);
  return { ...f, nodes };
});

/**
 * A C flow just named `main`, in a project with no entry, becomes the entry,
 * returning int, and its Contract declaration says int too.
 * Returns { flows, touched:Set } — flows unchanged when nothing applies.
 */
const _promoteNamedMain = (flows, flowId, projectLanguage) => {
  const touched = new Set();
  const flow = flows.find((f) => f.id === flowId);
  if (!flow || flow.flowType === 'contract' || flow.name !== 'main' || isEntryFlow(flow)) return { flows, touched };
  if (!_isCFlow(flow, projectLanguage)) return { flows, touched };
  if (flows.some((f) => f !== flow && !f.includedFrom && isEntryFlow(f))) return { flows, touched };
  let next = flows.map((f) => (f.id !== flowId ? f : {
    ...f,
    isEntry: true,
    flowType: 'main',
    returnType: 'int',
    _entryByName: { nodes: JSON.stringify(f.nodes || []), returnType: f.returnType, flowType: f.flowType },
  }));
  touched.add(flowId);
  next = _mapDeclsOf(next, flowId, (n) => (n.returnType === 'int' ? n : { ...n, returnType: 'int' }), touched);
  return { flows: next, touched };
};

/** Undo _promoteNamedMain for a flow renamed on before anything was built in it. */
const _demotePassingEntry = (flows, flowId) => {
  const touched = new Set();
  const flow = flows.find((f) => f.id === flowId);
  if (!flow || !_isPassingThroughMain(flow)) return { flows, touched };
  const prev = flow._entryByName;
  let next = flows.map((f) => {
    if (f.id !== flowId) return f;
    const { _entryByName, ...rest } = f;
    return { ...rest, isEntry: false, flowType: prev.flowType && prev.flowType !== 'main' ? prev.flowType : 'function', returnType: prev.returnType || 'void' };
  });
  touched.add(flowId);
  next = _mapDeclsOf(next, flowId, (n) => ({ ...n, returnType: prev.returnType || 'void' }), touched);
  return { flows: next, touched };
};

// ADO #266 — shape of a function_decl node seeded into a Contract Flow for
// the file's entry function. Matches what the contract editor produces for
// user-added function_decls (see math.c contract in any multi-file project),
// so downstream consumers (_emitContractFunctionDeclsC, contract-node UI)
// don't need to special-case auto-created entries.
const buildEntryFunctionDecl = (options, ts) => ({
  id: `function_decl-${options.entryFunctionName || 'main'}-${ts}`,
  type: 'function_decl',
  label: 'Function',
  hasError: false,
  scopeMode: 'inherit',
  parameters: options.entryParameters || [],
  returnType: options.entryReturnType || 'int',
  _implFlowId: options.entryImplFlowId,
  functionName: options.entryFunctionName || 'main',
});

export const createContractFlow = (sessionName, options = {}) => {
  // No Start node — Contract Flow is declaration, not executable logic.
  // Configuration node is mandatory at position 1 (auto-created).
  // End node = completeness anchor (mandatory, cannot be deleted).
  // ADO #266 — when `entryImplFlowId` is provided, also seed a function_decl
  // node for the entry function (typically `main` for C). This makes the
  // contract complete from day 1: every impl unit, including main, is
  // declared on the contract surface. The function_decl is wired to the
  // existing impl flow via _implFlowId.
  const ts = Date.now();
  const initialNodes = [
    {
      id: `config-${ts}`,
      type: 'configuration',
      label: 'Configuration',
      accessSpecifier: 'public',
      isAbstract: false,
      isFinal: false,
      extendsClass: '',
      implementsList: [],
      // ADO #244 — C track: file-level scope default.
      //   'this-file'        → static (internal, no .h declarations)
      //   'beyond-this-file' → extern (declarations emitted in generated .h)
      // ADO #262 follow-up — default flipped from 'this-file' to
      // 'beyond-this-file' for educational UX. Students declaring a
      // function in math.c expect to call it from main.c without first
      // discovering the scopeMode toggle. Power users can still mark
      // individual function_decls (or this Configuration) as 'this-file'
      // to get the static-by-default C behaviour.
      scopeMode: 'beyond-this-file',
      // ADO #244 Phase 3a — C++ inheritance access (public/private/protected)
      // and virtual inheritance flag. Defaults match C++ idiom (`public`).
      inheritanceAccess: 'public',
      isVirtualInheritance: false,
    },
    // Resources node — always visible in Contract Flow (data members).
    // Permanently positioned after Configuration. Cannot be deleted or moved.
    // Shows empty state when no fields declared.
    { id: `resources-${ts}`, type: 'resources', label: 'Data Members' },
    ...(options.entryImplFlowId ? [buildEntryFunctionDecl(options, ts)] : []),
    { id: `end-${ts}`, type: 'end', label: 'End' }
  ];
  const initialResources = [];
  return {
    id: `flow-${Date.now()}-contract-${Math.random().toString(36).substr(2, 5)}`,
    // ADO #390 — a Contract flow must NOT be named plainly after its session
    // (e.g. 'main'). That collides with the entry flow's name in the DB AND
    // trips the `flow.name === 'main'` entry-detection heuristics
    // (codeNextProgress, OutlinePanel, buildGuidedSteps). Prefix so it's
    // distinct and obviously a contract surface.
    name: sessionName ? `[Contract] ${sessionName}` : '[Contract]',
    isEntry: false,
    flowType: 'contract',
    accessSpecifier: 'public',
    parameters: [],
    returnType: 'void',
    nodes: initialNodes,
    resources: initialResources,
    history: [JSON.stringify({ nodes: initialNodes, resources: initialResources })],
    historyIndex: 0,
    skipHistoryPush: false,
    clipboard: null,
    clipboardAction: null,
    cutNodeId: null,
  };
};

// ADO #583 — the Contract appears only when a second function is added.
// Ram, 2026-10-20: "the Contract flow belongs to that file only, but hide it
// until another function is needed." A single C file whose Contract declares
// nothing but main (no other function, no data members, no globals) keeps the
// Contract in the store — it still declares main — but out of the tabs and the
// Outline. Anything the learner adds there brings it back, and so does opening
// it (the Global Resources card jumps to it). Multi-file projects, a shared
// project (My Toolbox, a capstone) and the Contract tour always show it.
const _LONE_CONTRACT_NODES = new Set(['configuration', 'resources', 'end']);
export const isContractHidden = (flow, state) => {
  if (!flow || flow.flowType !== 'contract' || !state || flow.includedFrom) return false;
  if (state.contractPinned || state.projectShared) return false;
  if (flow.id === state.activeFlowId) return false;
  const files = state.files || [];
  if (files.length !== 1 || files[0].includedFrom) return false;
  const file = files[0];
  if (!/\.c$/i.test(file.name || '')) return false;
  if (flow.fileName && flow.fileName !== file.name) return false;
  const session = (state.sessions || []).find((s) => s.id === flow.sessionId);
  if (!_isFreeFunctionsSession(session, file.name)) return false;
  if ((flow.resources || []).length > 0) return false;
  if ((state.globalResources || []).some((r) => !r.includedFrom)) return false;
  const members = (state.flows || []).filter((f) => f !== flow && f.flowType !== 'contract' && !f.includedFrom
    && (flow.sessionId && f.sessionId ? f.sessionId === flow.sessionId : (f.fileName || file.name) === file.name));
  if (members.length > 1 || members.some((f) => !isEntryFlow(f))) return false;
  const entryId = members[0]?.id;
  return (flow.nodes || []).every((n) => _LONE_CONTRACT_NODES.has(n.type)
    || (n.type === 'function_decl' && (n._implFlowId ? n._implFlowId === entryId : (n.functionName || 'main') === 'main')));
};

/** The ids of the Contract flows hidden right now, joined — a stable selector value. */
export const selectHiddenContractIds = (state) => (state.flows || [])
  .filter((f) => isContractHidden(f, state)).map((f) => f.id).join(',');

// The flow to open a session on: its Contract, unless that is hidden (#583),
// then its entry, then its first flow.
const _openingFlowOf = (state, sessionFlows) => {
  const contract = sessionFlows.find((f) => f.flowType === 'contract');
  if (contract && !isContractHidden(contract, { ...state, activeFlowId: null })) return contract;
  return sessionFlows.find((f) => isEntryFlow(f)) || sessionFlows.find((f) => f.flowType !== 'contract') || contract || null;
};

// A C file holds functions only, whatever kind its session was saved with:
// 44 projects' main.c sessions are stored as kind 'class' (2026-10-25), and
// treating them as classes kept New Flow's functions off the Contract — and
// the load-time orphan cleanup (#246) then deleted them.
const _isFreeFunctionsSession = (session, fileName) => (
  !session || session.kind === 'freeFunctions' || /\.c$/i.test(fileName || ''));

// ADO #583 — declare a flow just added to a C free-functions file on that
// file's Contract (before its End), shaped as addNode('function_decl') makes
// one: private to the file (#523). Returns { flows, contractId|null }.
const _declareOnContract = (flows, newFlow, sessions) => {
  if (!newFlow || !/\.c$/i.test(newFlow.fileName || '')) return { flows, contractId: null };
  const session = (sessions || []).find((s) => s.id === newFlow.sessionId);
  if (!_isFreeFunctionsSession(session, newFlow.fileName)) return { flows, contractId: null };
  const contract = flows.find((f) => f.flowType === 'contract' && !f.includedFrom
    && (newFlow.sessionId ? f.sessionId === newFlow.sessionId : f.fileName === newFlow.fileName));
  if (!contract || (contract.nodes || []).some((n) => n._implFlowId === newFlow.id)) return { flows, contractId: null };
  const decl = {
    id: `function_decl-${newFlow.name}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    type: 'function_decl',
    label: 'Function',
    hasError: false,
    scopeMode: 'this-file',
    functionName: newFlow.name,
    returnType: newFlow.returnType || 'void',
    parameters: (newFlow.parameters || []).map((p) => ({ name: p.name, type: p.type })),
    _implFlowId: newFlow.id,
  };
  const nodes = [...(contract.nodes || [])];
  const endIdx = nodes.findIndex((n) => n.type === 'end');
  nodes.splice(endIdx >= 0 ? endIdx : nodes.length, 0, decl);
  const resources = contract.resources || [];
  const history = [...(contract.history || []).slice(0, (contract.historyIndex ?? 0) + 1), JSON.stringify({ nodes, resources })];
  const next = { ...contract, nodes, history, historyIndex: history.length - 1 };
  return { flows: flows.map((f) => (f === contract ? next : f)), contractId: contract.id };
};

const initialFlow = createInitialFlow();

// Main Flow Store
const useFlowStore = create((set, get) => ({
  // Project Info
  projectName: 'My Program',
  projectId: null, // Backend project ID (null = not saved yet)
  allTestsPassed: false, // Tests panel: true when all practice tests pass
  isSaving: false,
  isLoading: false,
  lastSavedAt: null,
  // ADO #274 — dirty tracking. `hasUnsavedChanges` is the legacy boolean
  // (still consumed by VFB header + AgentChatWindow); `dirtyFlowIds` is
  // the per-flow set the project-wide saver targets, and
  // `projectMetaDirty` flags edits to project-level fields (project
  // name, files, sessions). All three are kept in lockstep — every
  // dirty signal sets the boolean too, every clear resets all three.
  hasUnsavedChanges: false,
  dirtyFlowIds: new Set(),
  projectMetaDirty: false,
  // ADO #580 — the loaded project's language ('c' | 'java' | ...), for rules
  // that hold for C only when a flow has no fileName to tell.
  projectLanguage: null,
  // ADO #583 — keep the Contract tab in view whatever it holds: a project
  // shared by several items (My Toolbox, a capstone), and the Contract tour.
  projectShared: false,
  contractPinned: false,
  
  // Multiple Flows (Functions) - each with own history
  flows: [initialFlow],
  activeFlowId: initialFlow.id,

  // Multi-file support (ADO #215)
  // files[] is empty for single-file projects (backward compatible).
  // Each file: { id, name, fileClassType, recordComponents }.
  files: [],
  activeFileId: null,

  // ADO #237 — Session concept. One session = one implementable unit
  // (class, record, nested class, sibling class, free/module functions).
  // A file can contain multiple sessions. Sessions are empty for
  // single-file legacy projects, which use an implicit session on read.
  //
  // Each session: {
  //   id, fileId, parentSessionId (for staticNested/innerClass),
  //   kind: 'class' | 'record' | 'freeFunctions' | 'moduleFunctions'
  //         | 'staticNested' | 'innerClass' | 'siblingClass',
  //   name, accessSpecifier, sessionData, displayOrder
  // }
  sessions: [],
  activeSessionId: null,

  // Global Resources (shared across all flows) - separate history
  globalResources: [],
  globalHistory: [JSON.stringify([])],
  // ADO #244 Phase 0 — when a contract node with an impl flow / nested
  // session is cascade-deleted, stash the deleted artifacts here keyed by
  // the contract node's id. Per-flow undo restores the node; this map lets
  // us re-attach the impl flow / session that the per-flow snapshot can't
  // see (flows[] and sessions[] live outside per-flow history).
  _implFlowTrash: {},
  // ADO #244 Phase 2.5 — backend ids of flows / sessions removed by cascade
  // delete that need to be DELETEd from the server on the next save.
  // Frontend-only ids (no UUID format) are skipped — they never hit the API.
  _pendingFlowDeletes: [],
  _pendingSessionDeletes: [],
  globalHistoryIndex: 0,
  
  // Setters
  setProjectName: (name) => set((state) => ({
    projectName: name,
    hasUnsavedChanges: true,
    projectMetaDirty: true,
  })),
  setProjectId: (id) => set({ projectId: id }),
  setAllTestsPassed: (val) => set({ allTestsPassed: val }),
  setIsSaving: (val) => set({ isSaving: val }),
  setIsLoading: (val) => set({ isLoading: val }),
  markSaved: () => set({
    lastSavedAt: new Date().toISOString(),
    hasUnsavedChanges: false,
    dirtyFlowIds: new Set(),
    projectMetaDirty: false,
  }),
  markUnsaved: () => set((state) => {
    const { activeFlowId, dirtyFlowIds } = state;
    if (!activeFlowId || dirtyFlowIds.has(activeFlowId)) {
      return { hasUnsavedChanges: true };
    }
    const next = new Set(dirtyFlowIds);
    next.add(activeFlowId);
    return { hasUnsavedChanges: true, dirtyFlowIds: next };
  }),

  // ADO #274 — dirty-tracking helpers. The save handler reads
  // `dirtyFlowIds` to know which flows need to round-trip on Save;
  // `projectMetaDirty` flags edits to files / sessions / project name.
  // Both flip `hasUnsavedChanges` for legacy consumers.
  markFlowDirty: (flowId) => set((state) => {
    if (!flowId || state.dirtyFlowIds.has(flowId)) {
      return state.hasUnsavedChanges ? {} : { hasUnsavedChanges: true };
    }
    const next = new Set(state.dirtyFlowIds);
    next.add(flowId);
    return { dirtyFlowIds: next, hasUnsavedChanges: true };
  }),
  markFlowsDirty: (flowIds) => set((state) => {
    if (!Array.isArray(flowIds) || flowIds.length === 0) return {};
    const next = new Set(state.dirtyFlowIds);
    let changed = false;
    for (const id of flowIds) {
      if (id && !next.has(id)) { next.add(id); changed = true; }
    }
    if (!changed) return state.hasUnsavedChanges ? {} : { hasUnsavedChanges: true };
    return { dirtyFlowIds: next, hasUnsavedChanges: true };
  }),
  markProjectMetaDirty: () => set({
    projectMetaDirty: true,
    hasUnsavedChanges: true,
  }),

  // Get active flow
  getActiveFlow: () => {
    const { flows, activeFlowId } = get();
    return flows.find(f => f.id === activeFlowId);
  },

  // ADO #246 Phase A — scoped resource projection for a flow.
  // Returns { instance, parameters, local } where:
  //   instance   — class fields (globalResources with location==='classField')
  //                + record components surfaced as implicit fields for record
  //                  files. Empty for sessions that have no class semantics
  //                  (C file, free functions, Python module, etc.).
  //   parameters — signature entries from flow.parameters, projected into
  //                resource-shaped objects so any picker can render them.
  //                Tagged with kind:'parameter' for labelling.
  //   local      — flow.resources (the flow-scoped ones) + any remaining
  //                globalResources that aren't class fields (C globals,
  //                Python module-level vars).
  //
  // Callers pass a flowId; omit to use the active flow. Returns the three
  // empty arrays if the flow can't be resolved.
  getScopedResources: (flowId) => {
    const state = get();
    const flow = flowId
      ? state.flows.find(f => f.id === flowId)
      : state.flows.find(f => f.id === state.activeFlowId);
    if (!flow) return { instance: [], parameters: [], local: [] };

    const globalResources = state.globalResources || [];
    const classFieldGlobals = globalResources.filter(r => r.location === 'classField');
    const nonClassGlobals = globalResources.filter(r => r.location !== 'classField');

    // Record components are instance fields in disguise — reuse the #230
    // helper so behaviour stays consistent across pickers.
    const recordComponents = getRecordComponentResources(flow, state.files || []) || [];

    const tag = (kind) => (r) => ({ ...r, kind });

    const instance = [
      ...classFieldGlobals.map(tag('instance')),
      ...recordComponents.map(tag('instance')),
    ];

    const parameters = (flow.parameters || [])
      .filter(p => p && p.name)
      .map(p => ({
        id: `param-${flow.id}-${p.name}`,
        name: p.name,
        dataType: p.type || p.dataType || 'unknown',
        resourceType: 'variable',
        isParameter: true,
        scope: 'parameter',
        kind: 'parameter',
      }));

    const local = [
      ...(flow.resources || []).filter(r => !r._fromParameter).map(tag('local')),
      ...nonClassGlobals.map(tag('local')),
    ];

    return { instance, parameters, local };
  },
  
  // Get nodes from active flow (for backward compatibility)
  get nodes() {
    const flow = get().getActiveFlow();
    return flow?.nodes || [];
  },
  
  // Get flowName from active flow (for backward compatibility)
  get flowName() {
    return get().projectName;
  },
  
  setFlowName: (name) => set({ projectName: name }),
  
  // Get resources from active flow (for backward compatibility)
  get resources() {
    const flow = get().getActiveFlow();
    return flow?.resources || [];
  },
  
  // Flow Management
  setActiveFlow: (flowId) => set({ activeFlowId: flowId }),

  // ADO #521 — show a flow wherever it lives: switch to its file and session
  // (the tab bar is scoped to them), then make it active. Quick open and "open
  // the called flow" reach flows in other files, which setActiveFlow alone
  // would leave invisible behind the current file's tabs.
  revealFlow: (flowId) => {
    const { flows, files, sessions } = get();
    const flow = flows.find(f => f.id === flowId);
    if (!flow) return;
    const session = flow.sessionId ? sessions.find(s => s.id === flow.sessionId) : null;
    const file = (session && files.find(f => f.id === session.fileId))
      || files.find(f => f.name === flow.fileName) || null;
    set({
      activeFileId: file ? file.id : get().activeFileId,
      activeSessionId: session ? session.id : (file ? (sessions.find(s => s.fileId === file.id && !s.parentSessionId)?.id || null) : get().activeSessionId),
      activeFlowId: flowId,
    });
  },

  // --- Multi-file CRUD (ADO #215) ---
  // ADO #237 — adding a file also creates a default primary session for it.
  // Session kind follows file.fileClassType.
  addFile: (name, options = {}) => {
    const { files, flows, sessions } = get();
    // ADO #229 — accept class-type metadata on file creation. C has no
    // classes: a .c file with no type given is a FREE-FUNCTIONS file. The Add
    // File dialogs pass no type, so every main.c was a 'class' - and a C
    // capstone project grew a Java-style "main" with Configuration / Data
    // members / Add member / Add type (found by Ram, 2026-09-28).
    const fileClassType = options.fileClassType
      || (/\.c$/i.test(name) ? 'freeFunctions' : 'class');
    const recordComponents = options.recordComponents || [];
    const fileId = `file-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;
    const newFile = {
      id: fileId,
      name,
      fileClassType,
      recordComponents,
    };

    // ADO #237/243/244 — primary session for this file. Session kind tracks
    // the file's structural type so the contract-flow palette + property
    // editors can adapt:
    //   class | record | interface | enum | header | freeFunctions
    // ADO #244 Phase 3d — .h / .hpp / .hh / .hxx extension → header session.
    // ADO #244 Phase 3e — fileClassType 'freeFunctions' → free-function .cpp
    //   (Configuration scope flag + Function nodes only, no class semantics).
    const typeName = name.replace(/\.(java|c|cpp|py|h|hpp|hh|hxx)$/i, '');
    const isHeader = /\.(h|hpp|hh|hxx)$/i.test(name);
    const sessionKind =
      isHeader ? 'header' :
      fileClassType === 'freeFunctions' ? 'freeFunctions' :
      fileClassType === 'record' ? 'record' :
      fileClassType === 'interface' ? 'interface' :
      fileClassType === 'enum' ? 'enum' :
      'class';
    const newSession = {
      id: `session-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
      fileId,
      parentSessionId: null,
      kind: sessionKind,
      name: typeName,
      accessSpecifier: 'public',
      sessionData: fileClassType === 'record' ? { recordComponents } : {},
      displayOrder: 0,
    };

    // ADO #242 — auto-create a Contract Flow tab for the new session.
    const contractFlow = createContractFlow(typeName);
    contractFlow.fileName = name;
    contractFlow.sessionId = newSession.id;

    // ADO #244 Phase 3b — C++ class auto-creates a Destructor on session
    // creation (per OQ5 Option A). Includes the destructor declaration node
    // in the contract flow + a sibling implementation flow tab named
    // ~ClassName. User can delete + re-add via palette (maxPerFlow: 1).
    // Auto-destructor only for cpp CLASS sessions, not freeFunctions / header.
    const isCppFile = /\.(cpp|cc|cxx)$/i.test(name);
    let autoDestructorImplFlow = null;
    if (isCppFile && sessionKind === 'class') {
      autoDestructorImplFlow = createInitialFlow(`~${typeName}`, false);
      autoDestructorImplFlow.flowType = 'destructor';
      autoDestructorImplFlow.fileName = name;
      autoDestructorImplFlow.sessionId = newSession.id;
      const destructorNode = {
        id: `node-dest-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
        type: 'destructor_decl',
        label: 'Destructor',
        accessSpecifier: 'public',
        isVirtual: false,
        _implFlowId: autoDestructorImplFlow.id,
      };
      // Insert before End (always last in initial contract flow)
      const insertAt = contractFlow.nodes.length - 1;
      contractFlow.nodes = [
        ...contractFlow.nodes.slice(0, insertAt),
        destructorNode,
        ...contractFlow.nodes.slice(insertAt),
      ];
      // Re-snapshot history so the auto-injected node is part of the baseline.
      contractFlow.history = [JSON.stringify({ nodes: contractFlow.nodes, resources: contractFlow.resources })];
    }

    // First file in a project with NO flows: there is nothing for an implicit
    // "main" file to hold, so the new file is the project's only file. (The
    // bootstrap below exists to re-home flows written before multi-file.)
    // Also when the new file IS main.c: existing flows move straight into it,
    // rather than into a second, class-shaped "main" beside it.
    if (files.length === 0 && (flows.length === 0 || /^main\.c$/i.test(name))) {
      const homed = flows.map(f => ({ ...f, fileName: f.fileName || name, sessionId: f.sessionId || newSession.id }));
      set({
        files: [newFile],
        sessions: [newSession],
        flows: autoDestructorImplFlow
          ? [...homed, contractFlow, autoDestructorImplFlow]
          : [...homed, contractFlow],
        activeFileId: newFile.id,
        activeSessionId: newSession.id,
        activeFlowId: contractFlow.id,
        hasUnsavedChanges: true,
        projectMetaDirty: true,
      });
    } else if (files.length === 0) {
      // First time adding a file: bootstrap — assign all existing flows to the implicit first file
      const defaultFileId = `file-${Date.now() - 1}-${Math.random().toString(36).substr(2, 5)}`;
      const defaultFile = {
        id: defaultFileId,
        name: 'main',
        fileClassType: 'class',
        recordComponents: [],
      };
      // Default session for the bootstrapped 'main' file
      const defaultSession = {
        id: `session-${Date.now() - 1}-${Math.random().toString(36).substr(2, 5)}`,
        fileId: defaultFileId,
        parentSessionId: null,
        kind: 'class',
        name: 'main',
        accessSpecifier: 'public',
        sessionData: {},
        displayOrder: 0,
      };
      // Contract flow for the bootstrapped default file too. ADO #266 —
      // seed a function_decl for main wired to the existing main impl flow
      // so the Contract surface is complete from day 1.
      const defaultEntryImpl = flows.find((f) => f.isEntry && f.flowType !== 'contract');
      const defaultContractFlow = createContractFlow('main', defaultEntryImpl ? {
        entryImplFlowId: defaultEntryImpl.id,
        entryFunctionName: defaultEntryImpl.name || 'main',
        entryReturnType: 'int',
        entryParameters: [],
      } : {});
      defaultContractFlow.fileName = defaultFile.name;
      defaultContractFlow.sessionId = defaultSession.id;
      // Re-snapshot history so the auto-injected function_decl is part of
      // the baseline (matches the contractFlow re-snapshot above).
      defaultContractFlow.history = [JSON.stringify({
        nodes: defaultContractFlow.nodes,
        resources: defaultContractFlow.resources,
      })];
      const updatedFlows = flows.map(f => ({
        ...f,
        fileName: f.fileName || defaultFile.name,
        sessionId: f.sessionId || defaultSession.id,
      }));
      set({
        files: [defaultFile, newFile],
        sessions: [defaultSession, newSession],
        flows: autoDestructorImplFlow
          ? [defaultContractFlow, ...updatedFlows, contractFlow, autoDestructorImplFlow]
          : [defaultContractFlow, ...updatedFlows, contractFlow],
        activeFileId: newFile.id,
        activeSessionId: newSession.id,
        activeFlowId: contractFlow.id,
        hasUnsavedChanges: true,
        projectMetaDirty: true,
      });
    } else {
      set({
        files: [...files, newFile],
        sessions: [...sessions, newSession],
        flows: autoDestructorImplFlow
          ? [...flows, contractFlow, autoDestructorImplFlow]
          : [...flows, contractFlow],
        activeFileId: newFile.id,
        activeSessionId: newSession.id,
        activeFlowId: contractFlow.id,
        hasUnsavedChanges: true,
        projectMetaDirty: true,
      });
    }
    return newFile;
  },

  renameFile: (fileId, newName) => {
    const { files, flows } = get();
    const file = files.find(f => f.id === fileId);
    if (!file) return;
    if (file.includedFrom) return; // ADO #516 — an included library is read-only
    const oldName = file.name;
    const { globalResources } = get();
    set({
      files: files.map(f => f.id === fileId ? { ...f, name: newName } : f),
      // Update all flows that belonged to the old file name
      flows: flows.map(f => f.fileName === oldName ? { ...f, fileName: newName } : f),
      // A global the file owns (C: declared in its header) keeps its owner.
      globalResources: (globalResources || []).map(r => (r.ownerFile === oldName ? { ...r, ownerFile: newName } : r)),
      hasUnsavedChanges: true,
      projectMetaDirty: true,
    });
  },

  // ADO #235 — update a record file's components (add / remove / rename / retype).
  // The backend call is performed separately by the caller; this just updates
  // the in-memory files array so the Resources node and validators re-render.
  // ADO #243 — also sync session.sessionData.recordComponents AND auto-create/
  // remove accessor flow tabs ([auto] flowType: 'accessor', one per component).
  updateFileComponents: (fileId, recordComponents) => {
    const { files, sessions, flows } = get();
    const file = files.find(f => f.id === fileId);
    if (!file) return;
    const components = recordComponents || [];
    const recordSession = sessions.find(s => s.fileId === fileId && s.kind === 'record');
    let updatedFlows = flows;
    let updatedSessions = sessions;
    if (recordSession) {
      // Sync session.sessionData.recordComponents
      updatedSessions = sessions.map(s => s.id === recordSession.id
        ? { ...s, sessionData: { ...(s.sessionData || {}), recordComponents: components } }
        : s);
      // Auto-create accessor flows for new components; remove for missing ones
      const existingAccessors = flows.filter(f => f.sessionId === recordSession.id && f.flowType === 'accessor');
      const componentNames = components.map(c => c.name);
      // Remove accessors whose component no longer exists
      const keptFlows = flows.filter(f => !(f.flowType === 'accessor' && f.sessionId === recordSession.id && !componentNames.includes(f.name)));
      // Add accessors for new components
      const newAccessors = components
        .filter(c => !existingAccessors.some(a => a.name === c.name))
        .map(c => {
          const af = createInitialFlow(c.name, false);
          af.flowType = 'accessor';
          af.accessSpecifier = 'public';
          af.returnType = c.type;
          af.parameters = [];
          af.fileName = file.name;
          af.sessionId = recordSession.id;
          af._auto = true; // marker for the [auto] badge in FlowTabs
          return af;
        });
      updatedFlows = [...keptFlows, ...newAccessors];
    }
    set({
      files: files.map(f => f.id === fileId
        ? { ...f, recordComponents: components }
        : f),
      sessions: updatedSessions,
      flows: updatedFlows,
      hasUnsavedChanges: true,
      projectMetaDirty: true,
    });
  },

  deleteFile: (fileId) => {
    const { files, flows, sessions, activeFileId } = get();
    if (files.length <= 1) return; // Can't delete the last file
    const file = files.find(f => f.id === fileId);
    if (!file) return;
    if (file.includedFrom) return; // ADO #516 — stop including it instead (dropIncluded)
    // ADO #237 — cascade: sessions → flows
    const sessionsToDelete = sessions.filter(s => s.fileId === fileId).map(s => s.id);
    const newSessions = sessions.filter(s => s.fileId !== fileId);
    const newFlows = flows.filter(f => f.fileName !== file.name && !sessionsToDelete.includes(f.sessionId));
    const newFiles = files.filter(f => f.id !== fileId);
    // The file's own globals are not deleted with it: they become project-wide
    // again (_globals.h), so nothing that uses them stops compiling.
    const newGlobals = (get().globalResources || []).map(r => (r.ownerFile === file.name ? { ...r, ownerFile: null } : r));
    const newActiveFileId = activeFileId === fileId ? newFiles[0]?.id : activeFileId;
    const newActiveFile = newFiles.find(f => f.id === newActiveFileId);
    const flowsInNewActiveFile = newFlows.filter(f => f.fileName === newActiveFile?.name);
    const firstSessionInNewActiveFile = newSessions.find(s => s.fileId === newActiveFileId && !s.parentSessionId);
    set({
      files: newFiles,
      sessions: newSessions,
      flows: newFlows,
      globalResources: newGlobals,
      activeFileId: newActiveFileId,
      activeSessionId: firstSessionInNewActiveFile?.id || null,
      activeFlowId: flowsInNewActiveFile[0]?.id || newFlows[0]?.id,
      hasUnsavedChanges: true,
      projectMetaDirty: true,
    });
  },

  setActiveFile: (fileId) => {
    const { files, flows, sessions } = get();
    const file = files.find(f => f.id === fileId);
    if (!file) return;
    // ADO #237 — also pick the file's primary (top-level) session as active.
    const primarySession = sessions.find(s => s.fileId === fileId && !s.parentSessionId);
    // ADO #244 Phase 0 — prefer the session's Contract Flow as the active tab.
    const sessionFlows = primarySession ? flows.filter(f => f.sessionId === primarySession.id) : [];
    // ADO #583 — not a hidden Contract: the file opens on main() then.
    const openingFlow = _openingFlowOf(get(), sessionFlows);
    const fileFlows = flows.filter(f => f.fileName === file.name);
    set({
      activeFileId: fileId,
      activeSessionId: primarySession?.id || null,
      activeFlowId: openingFlow?.id || fileFlows[0]?.id || get().activeFlowId,
    });
  },

  // Helper: get the active file object
  getActiveFile: () => {
    const { files, activeFileId } = get();
    if (files.length === 0) return null;
    return files.find(f => f.id === activeFileId) || files[0];
  },

  // --- Session CRUD (ADO #237) ---
  // Validation rules (Gaps 2-6 from ADO #237) live in Phase 2 — for now
  // these are thin store actions that mirror the schema. Phase 2 wraps
  // them with the validation layer.

  setActiveSession: (sessionId) => {
    const { sessions, files, flows } = get();
    const session = sessions.find(s => s.id === sessionId);
    if (!session) return;
    // Also point activeFileId at the owning file
    const file = files.find(f => f.id === session.fileId);
    const sessionFlows = flows.filter(f => f.sessionId === sessionId);
    // ADO #244 Phase 0 — prefer the session's Contract Flow as the active tab,
    // unless it is hidden (ADO #583).
    const openingFlow = _openingFlowOf(get(), sessionFlows);
    set({
      activeSessionId: sessionId,
      activeFileId: file?.id || get().activeFileId,
      activeFlowId: openingFlow?.id || get().activeFlowId,
    });
  },

  // ADO #237 — addSession with Gaps 2-6 validation.
  // Returns { session } on success or { error } on validation failure.
  // Callers (outline UI) should toast.error(error) and skip the create.
  addSession: (fileId, sessionData = {}) => {
    const { sessions, files } = get();
    const file = files.find(f => f.id === fileId);
    if (!file) return { error: 'File not found' };

    const stripExt = (n) => (n || '').replace(/\.(java|c|cpp|py|h)$/i, '');
    const filenameStem = stripExt(file.name);
    const requestedKind = sessionData.kind || 'class';
    const requestedName = sessionData.name || filenameStem;
    const requestedAccess = sessionData.accessSpecifier || 'public';
    const requestedParent = sessionData.parentSessionId || null;
    // ADO #445 — a Python file is a MODULE (namespace): it may hold any number of
    // top-level classes + module functions, none with access specifiers and none
    // required to match the filename. The Java "one public class per file" rules
    // (Gap 2/3/6 below) are skipped for Python.
    const isPython = /\.py$/i.test(file.name);

    // Gap 4 — Interface / Enum guard
    if (file.fileClassType === 'interface' || file.fileClassType === 'enum') {
      return { error: 'Interface and Enum files do not have FlowBuilder sessions. They are managed in the outline only.' };
    }

    // Gap 5 — Nesting depth cap (one level)
    if (requestedParent) {
      const parent = sessions.find(s => s.id === requestedParent);
      if (parent && parent.parentSessionId) {
        return { error: 'Nested classes can only be one level deep. FlowBuilder does not support classes nested inside nested classes.' };
      }
    }

    const isPrimaryKind = (k) => k === 'class' || k === 'record';
    const fileSessions = sessions.filter(s => s.fileId === fileId);
    const existingPublicPrimary = fileSessions.find(
      s => isPrimaryKind(s.kind) && s.accessSpecifier === 'public'
    );

    // Gap 2 — One public type per file (Java only; a Python module has many).
    if (!isPython && isPrimaryKind(requestedKind) && requestedAccess === 'public' && existingPublicPrimary) {
      return { error: 'A public type already exists in this file. Only one public type is allowed per .java file. The new type will be package-private.' };
    }

    // Gap 3 — Filename must match primary public type (Java only).
    const willBePrimaryPublic = isPrimaryKind(requestedKind) && requestedAccess === 'public';
    if (!isPython && willBePrimaryPublic && requestedName !== filenameStem) {
      return { error: `The public class name must match the filename. Rename the class to match "${filenameStem}" or rename the file to match "${requestedName}".` };
    }

    // Gap 6 — siblingClass cannot be public
    let effectiveAccess = requestedAccess;
    if (requestedKind === 'siblingClass' && requestedAccess === 'public') {
      // Soft-downgrade with a console warning per ADO #237 Gap 6 wording
      console.warn('[ADO #237] siblingClass cannot be public; downgrading to package-private');
      effectiveAccess = 'package';
    }
    // siblingClass schema rules: parentSessionId null, name not == filename
    if (requestedKind === 'siblingClass') {
      if (requestedParent) {
        return { error: 'A sibling class is top-level — it cannot have a parent session.' };
      }
      if (requestedName === filenameStem) {
        return { error: `A sibling class name must differ from the filename "${filenameStem}" (the filename belongs to the primary public type).` };
      }
    }

    const newSession = {
      id: `session-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
      fileId,
      parentSessionId: requestedParent,
      kind: requestedKind,
      name: requestedName,
      accessSpecifier: effectiveAccess,
      sessionData: sessionData.sessionData || {},
      displayOrder: sessionData.displayOrder ?? fileSessions.length,
    };
    set({
      sessions: [...sessions, newSession],
      hasUnsavedChanges: true,
      projectMetaDirty: true,
    });
    return { session: newSession };
  },

  // ADO #445 — add a top-level class to a file from the outline "Add type"
  // button. Creates the class session AND its Contract Flow (so the class has
  // a declaration surface immediately), then switches to it. Python allows any
  // number of public top-level classes per module; for other tracks a 2nd+
  // class becomes a (package-private) siblingClass per Java's one-public-type
  // rule. Returns { session, contractFlow } or { error }.
  addClass: (fileId, className) => {
    const { files, sessions } = get();
    const file = files.find(f => f.id === fileId);
    if (!file) return { error: 'File not found' };
    const name = (className || '').trim();
    if (!name) return { error: 'Class name is required' };
    const isPython = /\.py$/i.test(file.name);
    const isFirst = sessions.filter(s => s.fileId === fileId).length === 0;
    const req = (isPython || isFirst)
      ? { kind: 'class', name, accessSpecifier: 'public' }
      : { kind: 'siblingClass', name };
    const res = get().addSession(fileId, req);
    if (res.error) return res;
    const contractFlow = createContractFlow(name);
    contractFlow.fileName = file.name;
    contractFlow.sessionId = res.session.id;
    set(state => ({
      flows: [...state.flows, contractFlow],
      activeFileId: fileId,
      activeSessionId: res.session.id,
      activeFlowId: contractFlow.id,
      hasUnsavedChanges: true,
      projectMetaDirty: true,
    }));
    return { session: res.session, contractFlow };
  },

  // ADO #447 — auto-create attributes from constructor params. For each param
  // that isn't already a class field, create a classField resource and seed the
  // constructor's impl flow with `self.param = param`. Removes the #1 beginner
  // OOP friction (declaring the field + hand-wiring the assignment). Python.
  // Returns { created, assigned } or { error }.
  seedConstructorAttributes: (implFlowId, sessionId, params) => {
    const { flows, sessions, globalResources, dirtyFlowIds } = get();
    const implFlow = flows.find(f => f.id === implFlowId);
    if (!implFlow) return { error: 'Constructor body not found' };
    const session = sessions.find(s => s.id === sessionId);
    const fileId = session?.fileId;
    const named = (params || []).filter(p => p && (p.name || '').trim());
    if (named.length === 0) return { error: 'Add at least one parameter first' };

    // Existing class fields for this class (or legacy file-scoped ones) — reuse
    // them instead of creating duplicates.
    const existingFields = globalResources.filter(
      r => r.location === 'classField' && (r.sessionId === sessionId || (fileId && r.fileId === fileId))
    );
    // Field ids already assigned in this constructor — so a re-click doesn't
    // add a second self.x = x for the same field.
    const alreadyAssigned = new Set();
    (implFlow.nodes || []).forEach(n =>
      (n.assignments || []).forEach(a => { if (a.targetVariable) alreadyAssigned.add(a.targetVariable); })
    );
    const stamp = Date.now();
    const newResources = [];
    const assignments = [];
    named.forEach((p, i) => {
      const pname = p.name.trim();
      // Reuse an existing field of the same name; only create when missing.
      let field = existingFields.find(r => r.name === pname) || newResources.find(r => r.name === pname);
      if (!field) {
        field = {
          id: `res-${stamp}-${i}-${Math.random().toString(36).substr(2, 4)}`,
          name: pname,
          resourceType: 'variable',
          dataType: p.type || 'str',
          initialValue: '',
          location: 'classField',
          scope: 'global',
          isGlobalVariable: true,
          sessionId,
          ...(fileId ? { fileId } : {}),
        };
        newResources.push(field);
      }
      if (alreadyAssigned.has(field.id)) return; // already wired in this ctor
      assignments.push({
        id: `assign-${stamp}-${i}`,
        targetVariable: field.id,     // classField, resolved by id
        // fullPath `self.<name>` = what the picker stores for a class attribute;
        // the editor shows "self.name" and codegen keeps it (pySelfRefByResource
        // guards against double-prefixing).
        fullPath: `self.${pname}`,
        operator: '=',
        valueSource: 'variable',
        sourceVariable: pname,        // the param — not a resource, stays bare
        sourceFullPath: pname,        // lets the editor DISPLAY the param source
      });
    });

    if (assignments.length === 0) return { created: 0, assigned: 0 };

    const assignNode = {
      id: `node-${stamp}-${Math.random().toString(36).substr(2, 5)}`,
      type: 'assignment',
      label: 'Assignment',
      assignments,
    };
    const nodes = [...(implFlow.nodes || [])];
    const endIdx = nodes.findIndex(n => n.type === 'end');
    nodes.splice(endIdx >= 0 ? endIdx : nodes.length, 0, assignNode);
    const snapshot = JSON.stringify({ nodes, resources: implFlow.resources || [] });
    const nextDirty = new Set(dirtyFlowIds);
    nextDirty.add(implFlowId);

    set({
      globalResources: [...globalResources, ...newResources],
      flows: flows.map(f => f.id === implFlowId
        ? { ...f, nodes, history: [...(f.history || []).slice(0, (f.historyIndex ?? 0) + 1), snapshot], historyIndex: ((f.historyIndex ?? 0) + 1) }
        : f),
      hasUnsavedChanges: true,
      projectMetaDirty: true,
      dirtyFlowIds: nextDirty,
    });
    return { created: newResources.length, assigned: assignments.length };
  },

  // ADO #237 — updateSession with the same Gaps 2-6 invariants.
  // Returns { session } or { error }.
  updateSession: (sessionId, patch) => {
    const { sessions, files } = get();
    const current = sessions.find(s => s.id === sessionId);
    if (!current) return { error: 'Session not found' };
    const file = files.find(f => f.id === current.fileId);
    const stripExt = (n) => (n || '').replace(/\.(java|c|cpp|py|h)$/i, '');
    const filenameStem = file ? stripExt(file.name) : '';

    const next = { ...current, ...patch };
    const isPrimaryKind = (k) => k === 'class' || k === 'record';

    // Gap 5 — re-check nesting depth if parent changes
    if (next.parentSessionId && next.parentSessionId !== current.parentSessionId) {
      const parent = sessions.find(s => s.id === next.parentSessionId);
      if (parent && parent.parentSessionId) {
        return { error: 'Nested classes can only be one level deep. FlowBuilder does not support classes nested inside nested classes.' };
      }
    }

    // Gap 6 — siblingClass cannot become public via update
    if (next.kind === 'siblingClass' && next.accessSpecifier === 'public') {
      console.warn('[ADO #237] siblingClass cannot be public; downgrading to package-private');
      next.accessSpecifier = 'package';
    }

    // Gap 2 — if becoming public primary, ensure no other public primary exists
    const becomingPublicPrimary = isPrimaryKind(next.kind) && next.accessSpecifier === 'public';
    if (becomingPublicPrimary) {
      const otherPublic = sessions.find(
        s => s.id !== sessionId
          && s.fileId === current.fileId
          && isPrimaryKind(s.kind)
          && s.accessSpecifier === 'public'
      );
      if (otherPublic) {
        return { error: 'A public type already exists in this file. Only one public type is allowed per .java file.' };
      }
    }

    // Gap 3 — if it's the primary public type, name must match filename
    if (becomingPublicPrimary && next.name !== filenameStem) {
      return { error: `The public class name must match the filename. Rename the class to match "${filenameStem}" or rename the file to match "${next.name}".` };
    }

    // siblingClass invariants
    if (next.kind === 'siblingClass') {
      if (next.parentSessionId) {
        return { error: 'A sibling class is top-level — it cannot have a parent session.' };
      }
      if (next.name === filenameStem) {
        return { error: `A sibling class name must differ from the filename "${filenameStem}".` };
      }
    }

    set({
      sessions: sessions.map(s => s.id === sessionId ? next : s),
      hasUnsavedChanges: true,
      projectMetaDirty: true,
    });
    return { session: next };
  },

  deleteSession: (sessionId) => {
    const { sessions, flows, activeSessionId } = get();
    const session = sessions.find(s => s.id === sessionId);
    if (!session) return;
    if (session.includedFrom) return; // ADO #516 — part of an included library
    // Cascade: delete nested sessions + flows belonging to this session (and any nested children)
    const nestedIds = sessions.filter(s => s.parentSessionId === sessionId).map(s => s.id);
    const allDeletedIds = [sessionId, ...nestedIds];
    const newSessions = sessions.filter(s => !allDeletedIds.includes(s.id));
    const newFlows = flows.filter(f => !allDeletedIds.includes(f.sessionId));
    let newActiveSessionId = activeSessionId;
    if (allDeletedIds.includes(activeSessionId)) {
      // pick a sibling primary session in the same file, fall back to any session
      const sibling = newSessions.find(s => s.fileId === session.fileId && !s.parentSessionId);
      newActiveSessionId = sibling?.id || newSessions[0]?.id || null;
    }
    set({
      sessions: newSessions,
      flows: newFlows,
      activeSessionId: newActiveSessionId,
      hasUnsavedChanges: true,
      projectMetaDirty: true,
    });
  },

  // Selector: all sessions for a file, ordered by displayOrder.
  getSessionsForFile: (fileId) => {
    const { sessions } = get();
    return sessions
      .filter(s => s.fileId === fileId)
      .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
  },

  getActiveSession: () => {
    const { sessions, activeSessionId } = get();
    return sessions.find(s => s.id === activeSessionId) || null;
  },

  // Helper: get flows scoped to the active file (or all flows for single-file)
  getActiveFileFlows: () => {
    const { files, flows } = get();
    if (files.length === 0) return flows; // Single-file: all flows
    const activeFile = get().getActiveFile();
    if (!activeFile) return flows;
    return flows.filter(f => f.fileName === activeFile.name);
  },

  addFlow: (signature = null) => {
    const { flows } = get();
    // ADO #516 — nothing is added to an included library from here.
    if (get().getActiveFile()?.includedFrom) return null;
    const count = flows.length;
    const fnName = signature?.name || `function${count}`;
    const newFlow = createInitialFlow(fnName, false);

    // Apply explicit signature: parameters + returnType
    if (signature) {
      newFlow.parameters = signature.parameters || [];
      newFlow.returnType = signature.returnType || 'void';

      // ADO #224 — flowType and accessSpecifier from the signature dialog.
      // Keep isStatic in sync with flowType so legacy readers (FlowTabs
      // `inst` badge, existing code gen) continue to work correctly.
      if (VALID_FLOW_TYPES.includes(signature.flowType)) {
        newFlow.flowType = signature.flowType;
        newFlow.isStatic = signature.flowType === 'static';
      }
      if (VALID_ACCESS.includes(signature.accessSpecifier)) {
        newFlow.accessSpecifier = signature.accessSpecifier;
      }

      // Mirror parameters as resources so existing nodes can reference them.
      // Tag with _fromParameter so the resources panel hides delete button
      // and the code generator skips them as locals.
      const paramResources = (signature.parameters || []).map((p) => ({
        id: `res-param-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        resourceType: 'variable',
        name: p.name,
        dataType: p.type || 'int',
        initialValue: '',
        scope: 'local',
        _fromParameter: true,
      }));
      newFlow.resources = paramResources;
    }

    // Assign fileName if multi-file project is active
    const activeFile = get().getActiveFile();
    if (activeFile) {
      newFlow.fileName = activeFile.name;
    }
    // ADO #237 — bind new flow to the active session when one exists.
    // Single-file legacy projects have activeSessionId === null and rely on
    // the file-name fallback in FlowTabs scoping.
    const { activeSessionId } = get();
    if (activeSessionId) {
      newFlow.sessionId = activeSessionId;
    }

    // ADO #273 — auto-heal broken references when a flow with the
    // restored name reappears. Walks every existing flow, clears
    // `_brokenReference` markers whose name matches, and repoints
    // Subflow targetFlowId to the new flow's id so the call wires up.
    const restored = { id: newFlow.id, name: fnName };
    const healedFlows = [...flows, newFlow].map((f) => {
      const updated = _healBrokenFlowRefs(f, restored);
      return updated || f;
    });

    // ADO #274 — adding a flow grows the project (meta-dirty), the new
    // flow has unsaved content (its initial nodes), and any flows that
    // were healed by the auto-heal pass had their nodes mutated and so
    // are dirty too.
    const dirtyAfterHeal = new Set(get().dirtyFlowIds);
    dirtyAfterHeal.add(newFlow.id);
    healedFlows.forEach((f, i) => {
      const original = i < flows.length ? flows[i] : null;
      if (original && original !== f) dirtyAfterHeal.add(f.id);
    });
    // ADO #583 — in a C file, a function added from New Flow (or the Outline)
    // is declared on its file's Contract, exactly as one added on the Contract
    // is. That is what brings a hidden Contract back with the new function in
    // it — and without a declaration the load-time orphan cleanup (#246)
    // dropped the flow on the next reload.
    const declared = _declareOnContract(healedFlows, newFlow, get().sessions);
    if (declared.contractId) dirtyAfterHeal.add(declared.contractId);
    // ADO #580 — a new flow named main, in a project with no entry, is the entry.
    const promoted = _promoteNamedMain(declared.flows, newFlow.id, get().projectLanguage);
    promoted.touched.forEach((id) => dirtyAfterHeal.add(id));
    set({
      flows: promoted.flows,
      activeFlowId: newFlow.id,
      dirtyFlowIds: dirtyAfterHeal,
      projectMetaDirty: true,
      hasUnsavedChanges: true,
    });
    return promoted.flows.find((f) => f.id === newFlow.id) || newFlow;
  },

  /**
   * Update a flow's function signature (name, parameters, returnType).
   * Reconciles parameter-mirrored resources: adds new ones, removes deleted ones,
   * updates type changes. Resources NOT marked _fromParameter are left alone.
   */
  updateFlowSignature: (flowId, signature) => {
    const { flows } = get();
    const flow = flows.find(f => f.id === flowId);
    if (!flow) return false;
    // ADO #580 — the entry is not renamed into another function.
    const refusal = entryRenameRefusal(flow, signature?.name || flow.name);
    if (refusal) { _refuse(refusal); return false; }
    // ...nor turned into another kind of flow.
    if (isEntryFlow(flow) && !_isPassingThroughMain(flow) && signature?.flowType && signature.flowType !== 'main') {
      signature = { ...signature, flowType: undefined };
    }

    const newParams = signature.parameters || [];
    const newParamNames = new Set(newParams.map(p => p.name));

    // Keep all non-parameter-mirrored resources untouched
    const nonParamResources = (flow.resources || []).filter(r => !r._fromParameter);
    // Existing parameter-mirrored resources keyed by name (for reuse)
    const existingParamResources = {};
    (flow.resources || []).forEach(r => {
      if (r._fromParameter) existingParamResources[r.name] = r;
    });

    // Build new parameter resources, reusing existing IDs where possible
    const updatedParamResources = newParams.map(p => {
      const existing = existingParamResources[p.name];
      if (existing) {
        return { ...existing, dataType: p.type || existing.dataType };
      }
      return {
        id: `res-param-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        resourceType: 'variable',
        name: p.name,
        dataType: p.type || 'int',
        initialValue: '',
        scope: 'local',
        _fromParameter: true,
      };
    });

    const newName = signature.name || flow.name;
    const nameChanged = newName !== flow.name;

    const renamedFlows = flows.map(f => f.id === flowId ? {
      ...f,
      name: newName,
      parameters: newParams,
      returnType: signature.returnType || 'void',
      // ADO #224 — update flowType and accessSpecifier when provided.
      // Mirror flowType → isStatic for legacy readers (inst badge, code gen).
      ...(VALID_FLOW_TYPES.includes(signature.flowType) ? {
        flowType: signature.flowType,
        isStatic: signature.flowType === 'static',
      } : {}),
      ...(VALID_ACCESS.includes(signature.accessSpecifier) ? {
        accessSpecifier: signature.accessSpecifier,
      } : {}),
      resources: [...nonParamResources, ...updatedParamResources],
    } : f);

    // ADO #273 — heal broken references when the rename matches a name
    // that was previously deleted. Mirrors the addFlow heal path.
    const finalFlows = nameChanged
      ? renamedFlows.map((f) => {
          const updated = _healBrokenFlowRefs(f, { id: flowId, name: newName });
          return updated || f;
        })
      : renamedFlows;

    // ADO #274 — signature change is per-flow content; healing on rename
    // dirties additional flows whose Subflow nodes were repointed.
    const dirtyAfterRename = new Set(get().dirtyFlowIds);
    dirtyAfterRename.add(flowId);
    if (nameChanged) {
      finalFlows.forEach((f, i) => {
        const original = renamedFlows[i];
        if (original !== f) dirtyAfterRename.add(f.id);
      });
    }
    // ADO #580 — named main with no entry: the entry. Renamed on from a
    // just-named main: a function again.
    let entryFixed = { flows: finalFlows, touched: new Set() };
    if (nameChanged) {
      entryFixed = newName === 'main'
        ? _promoteNamedMain(finalFlows, flowId, get().projectLanguage)
        : _demotePassingEntry(finalFlows, flowId);
    }
    entryFixed.touched.forEach((id) => dirtyAfterRename.add(id));
    set({
      flows: entryFixed.flows,
      dirtyFlowIds: dirtyAfterRename,
      hasUnsavedChanges: true,
    });
    return true;
  },

  updateFlow: (flowId, updates) => {
    const { flows, dirtyFlowIds } = get();
    const flow = flows.find((f) => f.id === flowId);
    // ADO #580 — the entry is not renamed into another function.
    if (flow && updates && typeof updates.name === 'string' && updates.name !== flow.name) {
      const refusal = entryRenameRefusal(flow, updates.name);
      if (refusal) {
        _refuse(refusal);
        const { name, ...rest } = updates;
        if (Object.keys(rest).length === 0) return false;
        updates = rest;
      }
    }
    let nextDirty = dirtyFlowIds.has(flowId)
      ? dirtyFlowIds
      : new Set(dirtyFlowIds).add(flowId);
    let nextFlows = flows.map(f => f.id === flowId ? { ...f, ...updates } : f);
    if (flow && typeof updates.name === 'string' && updates.name !== flow.name) {
      const fixed = updates.name === 'main'
        ? _promoteNamedMain(nextFlows, flowId, get().projectLanguage)
        : _demotePassingEntry(nextFlows, flowId);
      if (fixed.touched.size) {
        nextFlows = fixed.flows;
        nextDirty = new Set([...nextDirty, ...fixed.touched]);
      }
    }
    set({
      flows: nextFlows,
      dirtyFlowIds: nextDirty,
      hasUnsavedChanges: true,
    });
    return true;
  },

  deleteFlow: (flowId) => {
    const { flows, activeFlowId, dirtyFlowIds } = get();
    const flow = flows.find(f => f.id === flowId);
    if (flow?.includedFrom) return; // ADO #516 — an included library's flow
    if (flow?.isEntry) return; // Can't delete entry flow
    if (flow?.flowType === 'contract') return; // ADO #242 — contract flow is permanent
    if (flow?.flowType === 'accessor') return; // ADO #243 — accessor flows are auto-managed

    const newFlows = flows.filter(f => f.id !== flowId);
    // ADO #274 — drop the deleted flow from the dirty set if present;
    // mark project meta dirty since the flows[] structure shrank.
    const nextDirty = new Set(dirtyFlowIds);
    nextDirty.delete(flowId);
    set({
      flows: newFlows,
      activeFlowId: activeFlowId === flowId ? newFlows[0]?.id : activeFlowId,
      dirtyFlowIds: nextDirty,
      projectMetaDirty: true,
      hasUnsavedChanges: true,
    });
  },

  // ADO #273 — Force-delete with broken-reference markers.
  //
  // Walks every flow in the project and tags any Subflow / Action node
  // referring to the about-to-be-deleted flow with `_brokenReference`.
  // The canvas already has rendering paths for that marker (red badge).
  // Then performs the same removal as deleteFlow.
  //
  // The student must resolve every broken reference before tests can
  // run (test-runner gate lands in a sibling slice).
  forceDeleteFlow: (flowId) => {
    const { flows, activeFlowId } = get();
    const flow = flows.find(f => f.id === flowId);
    if (!flow) return;
    if (flow.isEntry) return;
    if (flow.flowType === 'contract') return;
    if (flow.flowType === 'accessor') return;

    const flowName = flow.name;
    const taggedFlowIds = new Set();
    const taggedFlows = flows.map((other) => {
      if (other.id === flowId) return other;
      const updated = _tagBrokenFlowRefs(other, { id: flowId, name: flowName });
      if (updated) taggedFlowIds.add(updated.id);
      return updated || other;
    });
    const newFlows = taggedFlows.filter(f => f.id !== flowId);
    // ADO #274 — every flow whose nodes were tagged with a broken-ref
    // marker now has unsaved changes; the deleted flow drops out of the
    // dirty set; flows[] structure shrank → meta-dirty.
    const { dirtyFlowIds } = get();
    const nextDirty = new Set(dirtyFlowIds);
    nextDirty.delete(flowId);
    for (const id of taggedFlowIds) nextDirty.add(id);
    set({
      flows: newFlows,
      activeFlowId: activeFlowId === flowId ? newFlows[0]?.id : activeFlowId,
      hasUnsavedChanges: true,
      dirtyFlowIds: nextDirty,
      projectMetaDirty: true,
    });
  },

  // ADO #273 — Force-delete a file. Tags broken refs across the whole
  // project for every flow the file owns, then cascades through the
  // existing file-delete logic.
  forceDeleteFile: (fileId) => {
    const { files, flows, sessions, activeFileId } = get();
    if (files.length <= 1) return;
    const file = files.find(f => f.id === fileId);
    if (!file) return;

    const sessionsToDelete = sessions.filter(s => s.fileId === fileId).map(s => s.id);
    const inFile = (f) => f.fileName === file.name || sessionsToDelete.includes(f.sessionId);
    const deletedTargets = flows.filter(inFile).map(f => ({ id: f.id, name: f.name }));

    const taggedFlowIds = new Set();
    const taggedFlows = flows.map((other) => {
      if (inFile(other)) return other; // about to be deleted
      let result = other;
      for (const t of deletedTargets) {
        const updated = _tagBrokenFlowRefs(result, t);
        if (updated) result = updated;
      }
      if (result !== other) taggedFlowIds.add(result.id);
      return result;
    });

    const newSessions = sessions.filter(s => s.fileId !== fileId);
    const newFlows = taggedFlows.filter(f => !inFile(f));
    const newFiles = files.filter(f => f.id !== fileId);
    // The file's own globals are not deleted with it: they become project-wide
    // again (_globals.h), so nothing that uses them stops compiling.
    const newGlobals = (get().globalResources || []).map(r => (r.ownerFile === file.name ? { ...r, ownerFile: null } : r));
    const newActiveFileId = activeFileId === fileId ? newFiles[0]?.id : activeFileId;
    // ADO #274 — every tagged flow becomes dirty; deleted flows drop
    // out of the dirty set; meta-dirty since files / sessions / flows
    // all shrank.
    const { dirtyFlowIds } = get();
    const nextDirty = new Set(dirtyFlowIds);
    for (const t of deletedTargets) nextDirty.delete(t.id);
    for (const id of taggedFlowIds) nextDirty.add(id);
    set({
      files: newFiles,
      sessions: newSessions,
      flows: newFlows,
      globalResources: newGlobals,
      activeFileId: newActiveFileId,
      hasUnsavedChanges: true,
      dirtyFlowIds: nextDirty,
      projectMetaDirty: true,
    });
  },

  // ADO #273 — Force-delete a session (Java class / record / etc.).
  // Tags broken refs for every flow the session owns, then cascades
  // through the existing session-delete logic.
  forceDeleteSession: (sessionId) => {
    const { sessions, flows, activeSessionId } = get();
    const session = sessions.find(s => s.id === sessionId);
    if (!session) return;

    const nestedIds = sessions.filter(s => s.parentSessionId === sessionId).map(s => s.id);
    const allDeletedSessionIds = [sessionId, ...nestedIds];
    const inSession = (f) => allDeletedSessionIds.includes(f.sessionId);
    const deletedTargets = flows.filter(inSession).map(f => ({ id: f.id, name: f.name }));

    const taggedFlowIds = new Set();
    const taggedFlows = flows.map((other) => {
      if (inSession(other)) return other;
      let result = other;
      for (const t of deletedTargets) {
        const updated = _tagBrokenFlowRefs(result, t);
        if (updated) result = updated;
      }
      if (result !== other) taggedFlowIds.add(result.id);
      return result;
    });

    const newSessions = sessions.filter(s => !allDeletedSessionIds.includes(s.id));
    const newFlows = taggedFlows.filter(f => !inSession(f));
    let newActiveSessionId = activeSessionId;
    if (allDeletedSessionIds.includes(activeSessionId)) {
      const sibling = newSessions.find(s => s.fileId === session.fileId && !s.parentSessionId);
      newActiveSessionId = sibling?.id || newSessions[0]?.id || null;
    }
    // ADO #274 — same dirty-set bookkeeping as forceDeleteFile.
    const { dirtyFlowIds } = get();
    const nextDirty = new Set(dirtyFlowIds);
    for (const t of deletedTargets) nextDirty.delete(t.id);
    for (const id of taggedFlowIds) nextDirty.add(id);
    set({
      sessions: newSessions,
      flows: newFlows,
      activeSessionId: newActiveSessionId,
      hasUnsavedChanges: true,
      dirtyFlowIds: nextDirty,
      projectMetaDirty: true,
    });
  },

  duplicateFlow: (flowId) => {
    const { flows } = get();
    const flow = flows.find(f => f.id === flowId);
    if (!flow) return null;
    
    // Deep clone the flow
    const clonedFlow = JSON.parse(JSON.stringify(flow));
    const newFlow = {
      ...clonedFlow,
      id: `flow-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
      name: `${flow.name}_copy`,
      isEntry: false,
      // Reset history for the new flow
      history: [JSON.stringify({ nodes: clonedFlow.nodes, resources: clonedFlow.resources })],
      historyIndex: 0,
      skipHistoryPush: false
    };
    
    // ADO #274 — duplicate creates a new dirty flow + grows flows[].
    const nextDirty = new Set(get().dirtyFlowIds);
    nextDirty.add(newFlow.id);
    set({
      flows: [...flows, newFlow],
      activeFlowId: newFlow.id,
      dirtyFlowIds: nextDirty,
      projectMetaDirty: true,
      hasUnsavedChanges: true,
    });
    return newFlow;
  },

  // Push to history for ACTIVE FLOW (per-flow undo/redo)
  pushToHistory: () => {
    const { flows, activeFlowId, dirtyFlowIds } = get();
    const flowIndex = flows.findIndex(f => f.id === activeFlowId);
    if (flowIndex === -1) return;

    const flow = flows[flowIndex];

    // ADO #582 — undo/redo used to set `skipHistoryPush`, and this function
    // then swallowed the NEXT push: the student's first real edit after an
    // Undo was never recorded, so it could not be undone. The equality check
    // below already makes a push after undo/redo a no-op when nothing changed,
    // which is all the flag was for.

    const stateStr = JSON.stringify({ nodes: flow.nodes, resources: flow.resources });
    const currentStr = flow.history[flow.historyIndex];

    if (stateStr === currentStr) return;

    // Truncate future history and add new state
    const newHistory = flow.history.slice(0, flow.historyIndex + 1);
    newHistory.push(stateStr);

    // Limit history to 50 items
    if (newHistory.length > 50) newHistory.shift();

    const newFlows = [...flows];
    newFlows[flowIndex] = {
      ...flow,
      history: newHistory,
      historyIndex: newHistory.length - 1
    };
    // ADO #274 — every real content change marks the active flow dirty
    // so the project-wide saver picks it up. Guarded by the
    // stateStr === currentStr early-return above so undo / no-op
    // history pushes don't flip the flag.
    const nextDirty = dirtyFlowIds.has(activeFlowId)
      ? dirtyFlowIds
      : new Set(dirtyFlowIds).add(activeFlowId);
    set({
      flows: newFlows,
      dirtyFlowIds: nextDirty,
      hasUnsavedChanges: true,
    });
  },
  
  // Update nodes in active flow
  setNodes: (nodes) => {
    const { flows, activeFlowId, pushToHistory } = get();
    set({
      flows: flows.map(f => f.id === activeFlowId ? { ...f, nodes } : f)
    });
    pushToHistory();
  },
  
  // Global Resource operations (with separate history)
  addGlobalResource: (rawResource) => {
    // ADO #582 — a legacy-dialog resource (type 'ARRAY') gets its canonical resourceType.
    const resource = normalizeLegacyResource(rawResource);
    logWork('flow.resource_added', { type: resourceKind(resource), scope: 'global' });
    const { globalResources, globalHistory, globalHistoryIndex } = get();
    const newResources = [...globalResources, resource];
    const newHistory = globalHistory.slice(0, globalHistoryIndex + 1);
    newHistory.push(JSON.stringify(newResources));
    if (newHistory.length > 50) newHistory.shift();
    set({
      globalResources: newResources,
      globalHistory: newHistory,
      globalHistoryIndex: newHistory.length - 1,
      // Global resources are project-level (persisted via
      // replaceProjectGlobalResources), so mark the project dirty — without
      // this, adding/editing a global (e.g. a macro) left no unsaved-changes
      // signal, so Save looked clean and tab-close gave no warning.
      hasUnsavedChanges: true,
      projectMetaDirty: true,
    });
  },

  updateGlobalResource: (resourceId, updates) => {
    const { globalResources, globalHistory, globalHistoryIndex, flows, dirtyFlowIds } = get();
    const newResources = globalResources.map(r => r.id === resourceId ? withLegacyTypeUpdate(r, updates) : r);
    const newHistory = globalHistory.slice(0, globalHistoryIndex + 1);
    newHistory.push(JSON.stringify(newResources));
    if (newHistory.length > 50) newHistory.shift();

    // Input bugs (epic #581) — a rename carries Input member picks
    // (`student.roll` → `stud.roll`) in every flow where the name means this
    // global (no same-named local shadows it). A global rename has no Undo of
    // its own, so each flow's undo snapshots are rewritten too: an Undo can
    // then never bring back a path naming a struct that no longer exists.
    const oldName = globalResources.find(r => r.id === resourceId)?.name;
    const newName = updates && typeof updates.name === 'string' ? updates.name : oldName;
    let newFlows = flows;
    let nextDirty = dirtyFlowIds;
    if (oldName && newName && oldName !== newName) {
      newFlows = flows.map(f => {
        if ((f.resources || []).some(r => r && r.name === oldName)) return f;
        const nodes = renameInputPathsInNodes(f.nodes || [], oldName, newName);
        if (nodes === (f.nodes || [])) return f;
        const history = Array.isArray(f.history) ? f.history.map((snap) => {
          try {
            const st = JSON.parse(snap);
            const n2 = renameInputPathsInNodes(st.nodes || [], oldName, newName);
            return n2 === (st.nodes || []) ? snap : JSON.stringify({ ...st, nodes: n2 });
          } catch { return snap; }
        }) : f.history;
        if (!nextDirty.has(f.id)) nextDirty = new Set(nextDirty).add(f.id);
        return { ...f, nodes, history };
      });
    }
    set({
      globalResources: newResources,
      globalHistory: newHistory,
      globalHistoryIndex: newHistory.length - 1,
      ...(newFlows !== flows ? { flows: newFlows, dirtyFlowIds: nextDirty } : {}),
      hasUnsavedChanges: true,
      projectMetaDirty: true,
    });
  },

  deleteGlobalResource: (resourceId) => {
    const { globalResources, globalHistory, globalHistoryIndex } = get();
    const newResources = globalResources.filter(r => r.id !== resourceId);
    const newHistory = globalHistory.slice(0, globalHistoryIndex + 1);
    newHistory.push(JSON.stringify(newResources));
    if (newHistory.length > 50) newHistory.shift();
    set({
      globalResources: newResources,
      globalHistory: newHistory,
      globalHistoryIndex: newHistory.length - 1,
      hasUnsavedChanges: true,
      projectMetaDirty: true,
    });
  },
  
  // Local Resource operations (in active flow)
  addLocalResource: (rawResource) => {
    // ADO #582 — a legacy-dialog resource (type 'ARRAY') gets its canonical resourceType.
    const resource = normalizeLegacyResource(rawResource);
    // Declaring the data is as much of the build as wiring the nodes — a flow
    // with three variables and one node is four actions, not one. Kind only:
    // never the identifier or the value the learner chose.
    logWork('flow.resource_added', { type: resourceKind(resource), scope: 'local' });
    const { flows, activeFlowId, pushToHistory } = get();
    set({
      flows: flows.map(f => f.id === activeFlowId 
        ? { ...f, resources: [...(f.resources || []), resource] } 
        : f
      )
    });
    pushToHistory();
  },

  // Epic #581 — Input inline Add New: declare a local resource AND update a
  // node (the Input step ticking it) in ONE store update, so one Undo removes
  // both. History snapshots { nodes, resources } together.
  // `dropIds` (Ram, 2026-10-24): resources to remove in the same update — a
  // "keep the result in" moved from a variable this step made to a new one
  // takes the old one away with it, and one Undo brings it back.
  addLocalResourceAndUpdateNode: (rawResource, nodeId, nodeUpdates, dropIds = []) => {
    const resource = normalizeLegacyResource(rawResource);
    logWork('flow.resource_added', { type: resourceKind(resource), scope: 'local' });
    const drop = new Set(dropIds || []);
    const { flows, activeFlowId, pushToHistory } = get();
    set({
      flows: flows.map(f => f.id === activeFlowId
        ? {
            ...f,
            resources: [...(f.resources || []).filter(r => !drop.has(r.id)), resource],
            nodes: nodeId ? findAndUpdateNode(f.nodes || [], nodeId, nodeUpdates || {}) : f.nodes,
          }
        : f
      )
    });
    pushToHistory();
  },

  // Assignment / Input v2 delete (Ram, 2026-10-22): update a node AND drop the
  // local resources it declared that nothing uses any more, in ONE store
  // update, so one Undo brings back both the row and the variable.
  updateNodeAndRemoveLocalResources: (nodeId, nodeUpdates, resourceIds = []) => {
    const drop = new Set(resourceIds || []);
    const { flows, activeFlowId, pushToHistory } = get();
    set({
      flows: flows.map(f => f.id === activeFlowId
        ? {
            ...f,
            resources: drop.size ? (f.resources || []).filter(r => !drop.has(r.id)) : f.resources,
            nodes: nodeId ? findAndUpdateNode(f.nodes || [], nodeId, nodeUpdates || {}) : f.nodes,
          }
        : f
      )
    });
    pushToHistory();
  },

  updateLocalResource: (resourceId, updates) => {
    const { flows, activeFlowId, pushToHistory } = get();
    set({
      flows: flows.map(f => {
        if (f.id !== activeFlowId) return f;
        // Input bugs (epic #581) — a rename carries Input member picks
        // (`student.roll` → `stud.roll`, `students[i].gpa` → …) with it, in
        // this same update, so the one history snapshot below holds both and
        // one Undo reverts both.
        const oldName = (f.resources || []).find(r => r.id === resourceId)?.name;
        const newName = updates && typeof updates.name === 'string' ? updates.name : oldName;
        const nodes = (oldName && newName && oldName !== newName)
          ? renameInputPathsInNodes(f.nodes || [], oldName, newName)
          : f.nodes;
        return {
          ...f,
          nodes,
          resources: (f.resources || []).map(r => r.id === resourceId ? withLegacyTypeUpdate(r, updates) : r),
        };
      })
    });
    pushToHistory();
  },
  
  deleteLocalResource: (resourceId) => {
    const { flows, activeFlowId, pushToHistory } = get();
    set({
      flows: flows.map(f => f.id === activeFlowId 
        ? { ...f, resources: (f.resources || []).filter(r => r.id !== resourceId) }
        : f
      )
    });
    pushToHistory();
  },
  
  // Legacy resource operations (maps to local)
  addResource: (resource) => get().addLocalResource(resource),
  updateResource: (id, updates) => get().updateLocalResource(id, updates),
  deleteResource: (id) => get().deleteLocalResource(id),
  
  // Node operations
  addNode: (afterId, type) => {
    // ADO #523 — is the active Contract flow a C file's?
    const _af = get().flows.find((f) => f.id === get().activeFlowId);
    const isCFileContract = _af?.flowType === 'contract' && /\.c$/i.test(_af?.fileName || '');
    logWork('flow.node_added', { type });
    const { flows, activeFlowId, pushToHistory } = get();
    const flow = flows.find(f => f.id === activeFlowId);
    if (!flow) return null;
    
    const config = getNodeConfig(type);
    const newNode = {
      id: generateId(),
      type,
      label: config.label || type,
      ...(type === 'decision' && {
        outcomes: [
          // ADO #582 — no hidden condition: the student writes it (validation says so).
          { id: `outcome-${Date.now()}-1`, label: 'True', conditions: [], nodes: [] },
          { id: `outcome-${Date.now()}-default`, label: 'Default', isDefault: true, nodes: [] }
        ]
      }),
      // ADO #405 — switch reuses the `outcomes` shape (each non-default outcome
      // is a case with caseValue + breakAfter); seeded with one case + default.
      ...(type === 'switch' && {
        switchExpression: '',
        outcomes: [
          { id: `case-${Date.now()}-1`, label: 'case', caseValue: '', breakAfter: true, nodes: [] },
          { id: `case-${Date.now()}-default`, label: 'Default', isDefault: true, nodes: [] }
        ]
      }),
      ...(type === 'loop' && {
        loopBody: [],
        afterLast: [],
        collection: 'items',
        variable: 'item'
      }),
      // AI node defaults
      ...(type === 'config' && {
        provider: 'openai',
        model: 'gpt-4o',
        temperature: 0.7,
        maxTokens: 2048,
        apiKeySource: 'emergent'
      }),
      ...(type === 'prompt_template' && {
        role: 'system',
        template: ''
      }),
      ...(type === 'llm_call' && {
        configRef: '',
        systemPromptRef: '',
        userInput: '',
        outputVar: '',
        streaming: true
      }),
      ...(type === 'memory' && {
        memoryType: 'buffer',
        windowSize: 10,
        memoryKey: 'chat_history',
        returnMessages: true
      }),
      ...(type === 'tool_call' && {
        toolName: '',
        toolDescription: '',
        parameters: []
      }),
      ...(type === 'api_call' && {
        url: '',
        method: 'GET',
        headers: {},
        body: ''
      }),
      ...(type === 'json_parser' && {
        inputVar: '',
        jsonPath: '',
        outputVar: ''
      }),
      ...(type === 'try_catch' && {
        branches: [
          { id: `try-${Date.now()}`, type: 'try', label: 'try', nodes: [] },
          { id: `catch-${Date.now()}`, type: 'catch', label: 'catch', exceptionType: 'Exception', exceptionVar: 'e', nodes: [] }
        ],
        includeFinally: false,
        includeElse: false
      }),
      // ADO #242 — Contract Flow node defaults
      ...(type === 'inner_class_decl' && {
        innerClassName: '',
        innerClassKind: 'staticNested',
        accessSpecifier: 'private',
      }),
      // ADO #244 Phase 2 — Python: surface property/dunder sub-state on method
      // and abstractmethod on configuration's ABC mode. Defaults applied here
      // and refined per-language in the method_decl block below.
      ...(type === 'configuration' && {
        accessSpecifier: 'public',
        isAbstract: false,
        isFinal: false,
        extendsClass: '',     // class — single superclass
        extendsList: [],      // interface — multiple parent interfaces
        implementsList: [],   // class + record — interface contracts
      }),
      ...(type === 'constructor_decl' && {
        accessSpecifier: 'public',
        parameters: [],
        throwsList: [],
        callsSuper: false,
        callsThis: false,
        // ADO #244 Phase 3b — C++ specifics. Java/Python ignore these.
        isExplicit: false,
        initializerList: [],   // [{ member: 'name_', value: 'name' }]
      }),
      // ADO #244 Phase 3b — C++ destructor (no params, no return type, max 1)
      ...(type === 'destructor_decl' && {
        accessSpecifier: 'public',
        isVirtual: false,
      }),
      // ADO #244 Phase 3c — C++ operator overload (multiple allowed)
      ...(type === 'operator_decl' && {
        operatorSymbol: 'operator==',
        accessSpecifier: 'public',
        returnType: 'bool',
        parameters: [{ type: '', name: 'other' }],
        isConst: false,
      }),
      ...(type === 'method_decl' && (() => {
        // ADO #243 — Interface methods default to 'abstract'; class/record default to 'instance'
        const sess = get().getActiveSession();
        const sessionKind = sess?.kind || 'class';
        return {
          methodName: '',
          methodKind: sessionKind === 'interface' ? 'abstract' : 'instance',
          accessSpecifier: 'public',
          returnType: 'void',
          parameters: [],
          isFinal: false,
          isSynchronized: false,
          throwsList: [],
          isOverride: false,
          isDeprecated: false,
          // ADO #244 Phase 2 — Python sub-kinds (only meaningful for python class)
          dunderName: '',          // e.g. '__str__'
          includeSetter: false,    // Property: optional setter tab
          includeDeleter: false,   // Property: optional deleter tab
          isAbstractMethod: false, // @abstractmethod (only when class is ABC)
          // ADO #244 Phase 3a — C++ method modifiers
          isConst: false,          // const member function
          isVirtual: false,        // virtual keyword
          isOverrideCpp: false,    // override keyword (named to avoid Java isOverride collision)
        };
      })()),
      // ADO #243 — Compact constructor (Java Record): no params (auto from fields), no name
      ...(type === 'compact_constructor_decl' && {
        accessSpecifier: 'public',
        throwsList: [],
      }),
      // ADO #243 — Static initializer: no name, no params, no return type
      ...(type === 'static_init_decl' && {
        // intentionally minimal — only the body matters
      }),
      // ADO #244 — Function declaration (C / C++ free / Python module)
      ...(type === 'function_decl' && {
        functionName: '',
        returnType: 'void',
        parameters: [],
        // C scope flag: overrides Configuration node default
        //   'this-file'        → static (internal)
        //   'beyond-this-file' → extern (declared in .h)
        //   'inherit'          → use Configuration node default
        // ADO #523 — a function in a C file starts PRIVATE: sharing it through
        // the file's header is a deliberate choice, never a default.
        scopeMode: isCFileContract ? 'this-file' : 'inherit',
      })
    };
    
    // A Return ends its path, so an End follows it — unless the next node already is one (Ram, 2026-10-20: a
    // Return before main's own End gave two Ends in a row). Inserts at `at` in a copy of `list`.
    const withReturnEnd = (list, at, node) => {
      const out = [...list];
      const nextIsEnd = out[at] && out[at].type === 'end';
      out.splice(at, 0, ...(type === 'return' && !nextIsEnd ? [node, { id: generateId(), type: 'end', label: 'End' }] : [node]));
      return out;
    };

    // Complex node insertion logic
    const findAndAddNode = (nodeList, targetId) => {
      for (let i = 0; i < nodeList.length; i++) {
        const node = nodeList[i];
        
        if ((node.type === 'decision' || node.type === 'switch') && node.outcomes) {
          if (targetId.startsWith(node.id + '-branch-')) {
            const updatedOutcomes = processDecisionBranches(node, targetId);
            if (updatedOutcomes) {
              const newList = [...nodeList];
              newList[i] = { ...node, outcomes: updatedOutcomes };
              return newList;
            }
          }
          if (targetId === node.id + '-after-merge') {
            // ADO #212 — a Return here ends the path; withReturnEnd adds an End unless one already follows.
            return withReturnEnd(nodeList, i + 1, newNode);
          }
          for (let j = 0; j < node.outcomes.length; j++) {
            const branchNodes = node.outcomes[j].nodes || [];
            const updatedBranchNodes = findAndAddNode(branchNodes, targetId);
            if (updatedBranchNodes) {
              const newOutcomes = node.outcomes.map((o, idx) => idx === j ? { ...o, nodes: updatedBranchNodes } : o);
              const newList = [...nodeList];
              newList[i] = { ...node, outcomes: newOutcomes };
              return newList;
            }
          }
        }
        
        if (node.type === 'loop') {
          if (targetId.startsWith(node.id + '-loopBody') || targetId.startsWith(node.id + '-afterLast')) {
            const updatedLoop = processLoopBranches(node, targetId);
            if (updatedLoop) {
              const newList = [...nodeList];
              newList[i] = updatedLoop;
              return newList;
            }
          }
          if (targetId === node.id + '-after-loop-merge') {
            // ADO #212 — a Return here ends the path; withReturnEnd adds an End unless one already follows.
            return withReturnEnd(nodeList, i + 1, newNode);
          }
          const loopBody = node.loopBody || [];
          const updatedLoopBody = findAndAddNode(loopBody, targetId);
          if (updatedLoopBody) {
            const newList = [...nodeList];
            newList[i] = { ...node, loopBody: updatedLoopBody };
            return newList;
          }
          const afterLast = node.afterLast || [];
          const updatedAfterLast = findAndAddNode(afterLast, targetId);
          if (updatedAfterLast) {
            const newList = [...nodeList];
            newList[i] = { ...node, afterLast: updatedAfterLast };
            return newList;
          }
        }

        if (node.type === 'try_catch' && node.branches) {
          if (targetId.startsWith(node.id + '-branch-')) {
            const updatedBranches = processTryCatchBranches(node, targetId);
            if (updatedBranches) {
              const newList = [...nodeList];
              newList[i] = { ...node, branches: updatedBranches };
              return newList;
            }
          }
          if (targetId === node.id + '-after-trycatch-merge') {
            // ADO #212 — a Return here ends the path; withReturnEnd adds an End unless one already follows.
            return withReturnEnd(nodeList, i + 1, newNode);
          }
          for (let j = 0; j < node.branches.length; j++) {
            const branchNodes = node.branches[j].nodes || [];
            const updatedBranchNodes = findAndAddNode(branchNodes, targetId);
            if (updatedBranchNodes) {
              const newBranches = node.branches.map((b, idx) => idx === j ? { ...b, nodes: updatedBranchNodes } : b);
              const newList = [...nodeList];
              newList[i] = { ...node, branches: newBranches };
              return newList;
            }
          }
        }
      }
      return null;
    };

    const processDecisionBranches = (decisionNode, targetId) => {
      const outcomes = decisionNode.outcomes || [];
      // ADO #211 — when placing a Return node on a Decision branch,
      // auto-append an End node after it so the branch terminates visually.
      const autoEndAfterReturn = type === 'return';
      const makeEndNode = () => ({ id: generateId(), type: 'end', label: 'End' });

      for (let i = 0; i < outcomes.length; i++) {
        const branchNodes = outcomes[i].nodes || [];
        if (targetId === `${decisionNode.id}-branch-${i}`) {
          const nextIsEnd = (o0) => (o0.nodes || [])[0] && o0.nodes[0].type === 'end';
          return outcomes.map((o, idx) => (idx === i
            ? { ...o, nodes: [...(autoEndAfterReturn && !nextIsEnd(o) ? [newNode, makeEndNode()] : [newNode]), ...(o.nodes || [])] }
            : o));
        }
        const afterPattern = `${decisionNode.id}-branch-${i}-after-`;
        if (targetId.startsWith(afterPattern)) {
          const afterNodeId = targetId.replace(afterPattern, '');
          const nodeIndex = branchNodes.findIndex(n => n.id === afterNodeId);
          if (nodeIndex !== -1) {
            const newBranchNodes = [...branchNodes];
            const followedByEnd = newBranchNodes[nodeIndex + 1] && newBranchNodes[nodeIndex + 1].type === 'end';
            if (autoEndAfterReturn && !followedByEnd) {
              newBranchNodes.splice(nodeIndex + 1, 0, newNode, makeEndNode());
            } else {
              newBranchNodes.splice(nodeIndex + 1, 0, newNode);
            }
            return outcomes.map((o, idx) => idx === i ? { ...o, nodes: newBranchNodes } : o);
          }
        }
      }
      return null;
    };

    const processLoopBranches = (loopNode, targetId) => {
      const loopBody = loopNode.loopBody || [];
      const afterLast = loopNode.afterLast || [];
      
      if (targetId === `${loopNode.id}-loopBody`) {
        return { ...loopNode, loopBody: [newNode, ...loopBody] };
      }
      if (targetId === `${loopNode.id}-afterLast`) {
        return { ...loopNode, afterLast: [newNode, ...afterLast] };
      }
      const loopBodyAfterPattern = `${loopNode.id}-loopBody-after-`;
      if (targetId.startsWith(loopBodyAfterPattern)) {
        const afterNodeId = targetId.replace(loopBodyAfterPattern, '');
        const nodeIndex = loopBody.findIndex(n => n.id === afterNodeId);
        if (nodeIndex !== -1) {
          const newLoopBody = [...loopBody];
          newLoopBody.splice(nodeIndex + 1, 0, newNode);
          return { ...loopNode, loopBody: newLoopBody };
        }
      }
      const afterLastAfterPattern = `${loopNode.id}-afterLast-after-`;
      if (targetId.startsWith(afterLastAfterPattern)) {
        const afterNodeId = targetId.replace(afterLastAfterPattern, '');
        const nodeIndex = afterLast.findIndex(n => n.id === afterNodeId);
        if (nodeIndex !== -1) {
          const newAfterLast = [...afterLast];
          newAfterLast.splice(nodeIndex + 1, 0, newNode);
          return { ...loopNode, afterLast: newAfterLast };
        }
      }
      return null;
    };

    const processTryCatchBranches = (tryCatchNode, targetId) => {
      const branches = tryCatchNode.branches || [];
      for (let i = 0; i < branches.length; i++) {
        const branchNodes = branches[i].nodes || [];
        if (targetId === `${tryCatchNode.id}-branch-${i}`) {
          return branches.map((b, idx) => idx === i ? { ...b, nodes: [newNode, ...(b.nodes || [])] } : b);
        }
        const afterPattern = `${tryCatchNode.id}-branch-${i}-after-`;
        if (targetId.startsWith(afterPattern)) {
          const afterNodeId = targetId.replace(afterPattern, '');
          const nodeIndex = branchNodes.findIndex(n => n.id === afterNodeId);
          if (nodeIndex !== -1) {
            const newBranchNodes = [...branchNodes];
            newBranchNodes.splice(nodeIndex + 1, 0, newNode);
            return branches.map((b, idx) => idx === i ? { ...b, nodes: newBranchNodes } : b);
          }
        }
      }
      return null;
    };

    let updatedNodes = findAndAddNode(flow.nodes, afterId);
    
    if (!updatedNodes) {
      const index = flow.nodes.findIndex(n => n.id === afterId);
      if (index !== -1) {
        updatedNodes = [...flow.nodes];
        updatedNodes.splice(index + 1, 0, newNode);
      } else {
        updatedNodes = flow.nodes;
      }
    }
    
    // ADO #244 Phase 0 — defer pushToHistory until AFTER _implFlowId / _sessionId
    // are set on newNode below; otherwise the history snapshot (and therefore
    // the undo-restored node) has no link back to its impl flow / session,
    // and the cascade-delete trash lookup misses on undo.
    set({
      flows: flows.map(f => f.id === activeFlowId ? { ...f, nodes: updatedNodes } : f)
    });

    // ADO #242 — inner_class_decl creates a new session (not an impl tab).
    if (type === 'inner_class_decl') {
      const activeFile = get().getActiveFile();
      const { activeSessionId } = get();
      const className = newNode.innerClassName || 'InnerClass';
      const kind = newNode.innerClassKind === 'innerClass' ? 'innerClass' : 'staticNested';
      const result = get().addSession(activeFile?.id, {
        kind,
        name: className,
        accessSpecifier: newNode.accessSpecifier || 'private',
        parentSessionId: activeSessionId,
      });
      if (result?.session) {
        newNode._sessionId = result.session.id;
      }
    }

    // ADO #242/243/244 — auto-create implementation flow tab for declaration nodes.
    // Method node: skip when methodKind === 'abstract' (no body) or @abstractmethod
    // or pureVirtual (C++ — declared `= 0`, no implementation).
    // Python Property method: handled separately below — creates getter/setter/deleter tabs.
    const isPythonProperty = type === 'method_decl' && newNode.methodKind === 'property';
    const isPureVirtual = type === 'method_decl' && newNode.methodKind === 'pureVirtual';
    // ADO #244 Phase 3d — header sessions are declaration-only: every
    // contract node is just a signature, no impl tab created.
    const activeSessionForType = get().getActiveSession();
    const isHeaderSession = activeSessionForType?.kind === 'header';
    const createsImplTab = !isHeaderSession && (
      type === 'constructor_decl' ||
      type === 'compact_constructor_decl' ||
      type === 'static_init_decl' ||
      type === 'function_decl' ||
      type === 'destructor_decl' ||
      type === 'operator_decl' ||
      (type === 'method_decl' && newNode.methodKind !== 'abstract' && !newNode.isAbstractMethod && !isPythonProperty && !isPureVirtual)
    );
    if (createsImplTab) {
      const activeSession = get().getActiveSession();
      const sessionName = activeSession?.name || '';
      let implName;
      let implFlowType;
      if (type === 'constructor_decl') {
        implName = sessionName;
        implFlowType = 'constructor';
      } else if (type === 'compact_constructor_decl') {
        implName = sessionName; // compact constructor named after the record
        implFlowType = 'compactConstructor';
      } else if (type === 'static_init_decl') {
        implName = 'static_init';
        implFlowType = 'staticInitializer';
      } else if (type === 'destructor_decl') {
        implName = `~${sessionName}`;
        implFlowType = 'destructor';
      } else if (type === 'operator_decl') {
        implName = newNode.operatorSymbol || 'operator==';
        implFlowType = 'operator';
      } else if (type === 'function_decl') {
        implName = newNode.functionName || 'function';
        implFlowType = 'function';
      } else {
        // method_decl
        // ADO #244 Phase 2 — Python: dunder uses dunderName as the impl tab name.
        if (newNode.methodKind === 'dunder') {
          implName = newNode.dunderName || '__dunder__';
          implFlowType = 'dunderMethod';
        } else {
          implName = newNode.methodName || 'method';
          if (newNode.methodKind === 'static') implFlowType = 'staticMethod';
          else if (newNode.methodKind === 'default') implFlowType = 'defaultMethod';
          else if (newNode.methodKind === 'class') implFlowType = 'classMethod';
          else implFlowType = 'instance';
        }
      }
      const implFlow = createInitialFlow(implName, false);
      implFlow.flowType = implFlowType;
      implFlow.accessSpecifier = newNode.accessSpecifier || 'public';
      implFlow.parameters = newNode.parameters || [];
      // Decl types that carry a returnType field: method_decl, function_decl,
      // operator_decl. constructor_decl / destructor_decl / static_init_decl /
      // compact_constructor_decl don't return — keep 'void'.
      implFlow.returnType = ['method_decl', 'function_decl', 'operator_decl'].includes(type)
        ? (newNode.returnType || 'void')
        : 'void';
      // ADO #261 — mirror parameters into resources so the expression validator
      // (which reads flow.resources only) recognises them as declared. Matches
      // the FunctionSignatureDialog path in addFlow at L870-879.
      implFlow.resources = (implFlow.parameters || []).map((p) => ({
        id: `res-param-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        resourceType: 'variable',
        name: p.name,
        dataType: p.type || 'int',
        initialValue: '',
        scope: 'local',
        _fromParameter: true,
      }));
      // Bind to current file + session
      const activeFile = get().getActiveFile();
      if (activeFile) implFlow.fileName = activeFile.name;
      const { activeSessionId } = get();
      if (activeSessionId) implFlow.sessionId = activeSessionId;
      // Store a reference from the contract node to its impl flow
      newNode._implFlowId = implFlow.id;
      // Add the impl flow
      const currentFlows = get().flows;
      // ADO #274 — new impl flow is dirty + the contract owner (active
      // flow) had its node mutated, so dirty too; flows[] grew.
      const nextDirty = new Set(get().dirtyFlowIds);
      nextDirty.add(implFlow.id);
      if (activeFlowId) nextDirty.add(activeFlowId);
      set({
        flows: [...currentFlows, implFlow],
        hasUnsavedChanges: true,
        dirtyFlowIds: nextDirty,
        projectMetaDirty: true,
      });
    }

    // ADO #244 Phase 2 — Python @property method: getter always, setter / deleter
    // optional. Each gets its own flow tab. The contract node tracks them as
    // an array on _propertyFlowIds so cascade-delete + undo can round-trip
    // all three together (extending the existing _implFlowId trash logic).
    if (isPythonProperty) {
      const activeFile = get().getActiveFile();
      const { activeSessionId } = get();
      const baseName = newNode.methodName || 'prop';
      const kinds = [{ suffix: 'getter', flowType: 'propertyGetter' }];
      if (newNode.includeSetter) kinds.push({ suffix: 'setter', flowType: 'propertySetter' });
      if (newNode.includeDeleter) kinds.push({ suffix: 'deleter', flowType: 'propertyDeleter' });
      const newFlowsArr = [];
      const ids = [];
      for (const k of kinds) {
        const f = createInitialFlow(`${baseName}_${k.suffix}`, false);
        f.flowType = k.flowType;
        f.parameters = [];
        f.returnType = k.suffix === 'getter' ? (newNode.returnType || 'None') : 'None';
        if (activeFile) f.fileName = activeFile.name;
        if (activeSessionId) f.sessionId = activeSessionId;
        newFlowsArr.push(f);
        ids.push(f.id);
      }
      newNode._propertyFlowIds = ids;
      newNode._implFlowId = ids[0]; // primary tab = getter, so cascade-delete catches at least one
      // ADO #274 — every new property flow is dirty + the contract owner
      // (active flow) had its node mutated; flows[] grew.
      const nextDirty = new Set(get().dirtyFlowIds);
      for (const id of ids) nextDirty.add(id);
      if (activeFlowId) nextDirty.add(activeFlowId);
      set({
        flows: [...get().flows, ...newFlowsArr],
        hasUnsavedChanges: true,
        dirtyFlowIds: nextDirty,
        projectMetaDirty: true,
      });
    }

    // ADO #244 Phase 0 — propagate any _implFlowId / _sessionId mutations on
    // newNode back into the active flow's nodes array (the array contains a
    // reference, but a fresh object spread ensures the next pushToHistory
    // serialises the linked id), then push history once with the full node.
    {
      const propagated = get().flows.map(f =>
        f.id === activeFlowId
          ? { ...f, nodes: f.nodes.map(n => n.id === newNode.id ? { ...n, ...newNode } : n) }
          : f
      );
      set({ flows: propagated });
    }
    pushToHistory();

    return newNode;
  },

  // A step still to choose becomes the step the student picked (utils/placeholderStep.js):
  // the new step goes in right after the slot and the slot goes, as ONE undo step.
  // `type` is a stored node type (addStepAt maps the menu's File / Memory steps).
  replacePlaceholder: (placeholderId, type) => {
    const before = get().flows.find((f) => f.id === get().activeFlowId);
    const slot = before && findNodeById(before.nodes, placeholderId);
    if (!slot || slot.type !== 'placeholder') return null;
    const at = addIdAfter(before.nodes, placeholderId);
    const node = at && get().addNode(at, type);
    if (!node) return null;
    const goal = typeof slot.label === 'string' && slot.label.trim() && slot.label.trim() !== 'Step to choose' ? slot.label.trim() : '';
    // An item's check on the slot goes with its words (utils/goalCheck.js).
    const kept = { ...(goal ? { goal } : {}), ...(slot.check && typeof slot.check === 'object' ? { check: slot.check } : {}) };
    const { flows, activeFlowId } = get();
    set({
      flows: flows.map((f) => {
        if (f.id !== activeFlowId) return f;
        // The slot's words stay with the step that does it (node.goal).
        const nodes = Object.keys(kept).length ? findAndUpdateNode(findAndDeleteNode(f.nodes, placeholderId), node.id, kept)
          : findAndDeleteNode(f.nodes, placeholderId);
        const history = [...(f.history || [])];
        // addNode recorded the flow with both steps; that entry becomes the flow without the slot.
        if (history.length) history[f.historyIndex] = JSON.stringify({ nodes, resources: f.resources });
        return { ...f, nodes, history };
      }),
    });
    return { ...node, ...kept };
  },

  updateNode: (nodeId, updates) => {
    const { flows, activeFlowId, pushToHistory } = get();
    const flow = flows.find(f => f.id === activeFlowId);
    if (!flow) return;

    // ADO #580 — a Contract function_decl implemented by the entry flow can't
    // rename it into another function (`main` → `indexOf` turned the program's
    // start into a helper). Keep the name; the rest of the edit applies.
    const declBefore = flow.flowType === 'contract' ? findNodeById(flow.nodes, nodeId) : null;
    const declImplFlow = declBefore?.type === 'function_decl' && declBefore._implFlowId
      ? flows.find((f) => f.id === declBefore._implFlowId)
      : null;
    if (declImplFlow && updates && 'functionName' in updates
        && (updates.functionName || 'function') !== declImplFlow.name) {
      const refusal = entryRenameRefusal(declImplFlow, updates.functionName || 'function');
      if (refusal) {
        _refuse(refusal);
        updates = { ...updates, functionName: declBefore.functionName ?? declImplFlow.name };
      }
    }

    const updatedNodes = findAndUpdateNode(flow.nodes, nodeId, updates);
    let nextFlows = flows.map(f => f.id === activeFlowId ? { ...f, nodes: updatedNodes } : f);

    // ADO #244 polish — keep linked impl-flow tab names in sync with the
    // declaration node's display name. Triggers when the contract node's
    // name field actually changes (avoids no-op renames on every keystroke).
    const updatedNode = findNodeById(updatedNodes, nodeId);
    if (updatedNode) {
      const sess = get().getActiveSession();
      const sessionName = sess?.name || '';
      // Compute the canonical impl-tab name for this contract-node type.
      const tabName = (() => {
        switch (updatedNode.type) {
          case 'constructor_decl':         return sessionName;
          case 'compact_constructor_decl': return sessionName;
          case 'destructor_decl':          return `~${sessionName}`;
          case 'static_init_decl':         return 'static_init';
          case 'function_decl':            return updatedNode.functionName || 'function';
          case 'operator_decl':            return updatedNode.operatorSymbol || 'operator==';
          case 'method_decl':
            if (updatedNode.methodKind === 'dunder') return updatedNode.dunderName || '__dunder__';
            return updatedNode.methodName || 'method';
          default: return null;
        }
      })();
      if (tabName) {
        // Single-flow declarations (constructor, method, function, …)
        if (updatedNode._implFlowId) {
          nextFlows = nextFlows.map(f => f.id === updatedNode._implFlowId && f.name !== tabName ? { ...f, name: tabName } : f);
        }
        // Python @property fans out to getter / setter / deleter — rename
        // each by suffixing _getter / _setter / _deleter to the new base.
        if (Array.isArray(updatedNode._propertyFlowIds)) {
          const suffixForFlowType = { propertyGetter: 'getter', propertySetter: 'setter', propertyDeleter: 'deleter' };
          nextFlows = nextFlows.map(f => {
            if (!updatedNode._propertyFlowIds.includes(f.id)) return f;
            const suffix = suffixForFlowType[f.flowType];
            const wantedName = suffix ? `${tabName}_${suffix}` : tabName;
            return f.name === wantedName ? f : { ...f, name: wantedName };
          });
        }
      }

      // ADO #246 follow-up — when a contract node's parameters are edited
      // after creation, mirror them onto the linked impl flow. Without
      // this, getScopedResources() returns an empty `parameters` array for
      // the impl flow and the Assignment target picker never shows a
      // PARAMETERS group. Python @property's getter/setter/deleter use
      // their own param semantics, so skip the fan-out case.
      // ADO #261 — also reconcile the _fromParameter mirrors in
      // f.resources so the expression validator stays in sync. Mirrors
      // updateFlowSignature at L912-935: keep non-param resources, update
      // existing param resources by name, drop removed ones, append new.
      const isPythonProperty =
        updatedNode._propertyFlowIds && updatedNode._propertyFlowIds.length > 0;
      // ADO #308 follow-up — when a method_decl's methodKind changes
      // (e.g. student flips Kind from 'instance' to 'static' in the
      // Contract Flow editor), sync the linked impl flow's flowType.
      // Without this, the impl tab badge, Java method-header codegen
      // (`public static void` vs `public void`), AND the subflow
      // `this.` auto-promotion in ADO #308 all keep reading the stale
      // 'instance' flowType and emit wrong code.
      // Mirrors the implFlowType branching at L1900-1908 in the
      // initial-creation path so behaviour stays consistent.
      if (updatedNode._implFlowId && updatedNode.type === 'method_decl' && !isPythonProperty) {
        const nextFlowType = (() => {
          if (updatedNode.methodKind === 'dunder')   return 'dunderMethod';
          if (updatedNode.methodKind === 'static')   return 'staticMethod';
          if (updatedNode.methodKind === 'default')  return 'defaultMethod';
          if (updatedNode.methodKind === 'class')    return 'classMethod';
          return 'instance';
        })();
        nextFlows = nextFlows.map(f =>
          f.id === updatedNode._implFlowId && f.flowType !== nextFlowType
            ? { ...f, flowType: nextFlowType }
            : f
        );
      }

      if (updatedNode._implFlowId && Array.isArray(updatedNode.parameters) && !isPythonProperty) {
        const nextParams = updatedNode.parameters.map(p => ({ ...p }));
        nextFlows = nextFlows.map(f => {
          if (f.id !== updatedNode._implFlowId) return f;
          const nonParamResources = (f.resources || []).filter(r => !r._fromParameter);
          const existingByName = {};
          (f.resources || []).forEach(r => {
            if (r._fromParameter) existingByName[r.name] = r;
          });
          const updatedParamResources = nextParams.map(p => {
            const existing = existingByName[p.name];
            if (existing) {
              return { ...existing, dataType: p.type || existing.dataType };
            }
            return {
              id: `res-param-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
              resourceType: 'variable',
              name: p.name,
              dataType: p.type || 'int',
              initialValue: '',
              scope: 'local',
              _fromParameter: true,
            };
          });
          // Also reconcile returnType so contract edits to "returns int"
          // propagate to the impl flow (which the ReturnNodeEditor's
          // "Returning <type>" badge + return-value validation read).
          // Only decl types that carry a returnType field qualify;
          // constructor / destructor / static_init don't.
          const declTypesWithReturn = ['method_decl', 'function_decl', 'operator_decl'];
          // ADO #313 — same sync pattern for the throws clause. Java
          // method_decl + constructor_decl carry a `throwsTypes: string[]`
          // field; mirror it onto the impl flow so the signature emitter
          // can read it without crossing the contract↔impl boundary.
          const declTypesWithThrows = ['method_decl', 'constructor_decl'];
          const next = {
            ...f,
            parameters: nextParams,
            resources: [...nonParamResources, ...updatedParamResources],
          };
          if (declTypesWithReturn.includes(updatedNode.type) && typeof updatedNode.returnType === 'string') {
            next.returnType = updatedNode.returnType || 'void';
          }
          if (declTypesWithThrows.includes(updatedNode.type) && Array.isArray(updatedNode.throwsTypes)) {
            next.throwsTypes = [...updatedNode.throwsTypes];
          }
          // ADO #449 — mirror the variadic flag (function_decl only) so codegen's
          // signature emitters read flow.isVariadic across the contract↔impl
          // boundary. Same pattern as throwsTypes.
          if (updatedNode.type === 'function_decl') {
            next.isVariadic = !!updatedNode.isVariadic;
          }
          return next;
        });
      }
    }

    // ADO #580 — after the name + signature sync above: a function_decl whose
    // flow is now named main, in a project with no entry, makes that flow the
    // entry (returning int); one renamed on from a just-named main goes back.
    let touched = new Set();
    if (declImplFlow && updatedNode && updatedNode.type === 'function_decl') {
      const nowName = nextFlows.find((f) => f.id === declImplFlow.id)?.name;
      if (nowName !== declImplFlow.name) {
        const fixed = nowName === 'main'
          ? _promoteNamedMain(nextFlows, declImplFlow.id, get().projectLanguage)
          : _demotePassingEntry(nextFlows, declImplFlow.id);
        nextFlows = fixed.flows;
        touched = fixed.touched;
      }
    }

    if (touched.size) {
      set((state) => ({
        flows: nextFlows,
        dirtyFlowIds: new Set([...state.dirtyFlowIds, ...touched]),
        hasUnsavedChanges: true,
      }));
    } else {
      set({ flows: nextFlows });
    }
    pushToHistory();
  },
  
  deleteNode: (nodeId) => {
    logWork('flow.node_removed', {});
    const { flows, activeFlowId, sessions, pushToHistory } = get();
    const flow = flows.find(f => f.id === activeFlowId);
    if (!flow) return;

    // ADO #242 — in contract flows, Configuration, Resources, and End are permanent
    if (flow.flowType === 'contract') {
      const node = findNodeById(flow.nodes, nodeId);
      if (node && (node.type === 'configuration' || node.type === 'resources' || node.type === 'end')) return;

      // ADO #244 Phase 0 — integrity check + cascade delete for declarations
      // that own an implementation flow or a nested session. The deleted
      // artifacts are stashed in _implFlowTrash so per-flow undo can put
      // them back when it restores the contract node.
      if (node?._implFlowId) {
        // ADO #244 Phase 2 — Python @property may own multiple impl flows
        // (getter/setter/deleter); _propertyFlowIds collects them all.
        const ownedIds = node._propertyFlowIds && node._propertyFlowIds.length > 0
          ? node._propertyFlowIds
          : [node._implFlowId];
        const ownedFlows = flows.filter(f => ownedIds.includes(f.id));
        const hasUserContent = ownedFlows.some(f => (f.nodes || []).some(n => n.type !== 'start' && n.type !== 'end'));
        if (hasUserContent) {
          const label = ownedFlows.map(f => f.name).join(', ') || 'this implementation flow';
          // eslint-disable-next-line no-alert
          const ok = window.confirm(`Deleting will also delete: ${label}. Continue?`);
          if (!ok) return;
        }
        const updatedNodes = findAndDeleteNode(flow.nodes, nodeId);
        const remainingFlows = flows
          .filter(f => !ownedIds.includes(f.id))
          .map(f => f.id === activeFlowId ? { ...f, nodes: updatedNodes } : f);
        const trash = { ...get()._implFlowTrash };
        trash[nodeId] = {
          kind: 'impl',
          // Store ALL owned flows (1 for normal method/constructor, up to 3 for @property)
          flows: ownedFlows.map(f => JSON.parse(JSON.stringify(f))),
        };
        // ADO #244 Phase 2.5 — queue backend deletes for the impl flows we
        // just removed. Frontend-only ids (no UUID) are skipped.
        const isUuid = (id) => typeof id === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(id);
        const pendingFlowDeletes = [...get()._pendingFlowDeletes, ...ownedIds.filter(isUuid)];
        set({ flows: remainingFlows, _implFlowTrash: trash, _pendingFlowDeletes: pendingFlowDeletes });
        pushToHistory();
        return;
      }
      if (node?._sessionId) {
        // Inner class — a whole nested session + all its flows go with the node.
        const nestedSession = sessions.find(s => s.id === node._sessionId);
        const nestedFlows = flows.filter(f => f.sessionId === node._sessionId);
        const hasUserContent = nestedFlows.some(f => (f.nodes || []).some(n => n.type !== 'start' && n.type !== 'end' && n.type !== 'configuration' && n.type !== 'resources'));
        if (nestedSession) {
          // eslint-disable-next-line no-alert
          const ok = window.confirm(`Deleting "${nestedSession.name}" will also delete its session and ${nestedFlows.length} flow(s)${hasUserContent ? ' (with content)' : ''}. Continue?`);
          if (!ok) return;
        }
        const updatedNodes = findAndDeleteNode(flow.nodes, nodeId);
        const remainingFlows = flows
          .filter(f => f.sessionId !== node._sessionId)
          .map(f => f.id === activeFlowId ? { ...f, nodes: updatedNodes } : f);
        const remainingSessions = sessions.filter(s => s.id !== node._sessionId);
        const trash = { ...get()._implFlowTrash };
        trash[nodeId] = {
          kind: 'session',
          session: nestedSession ? JSON.parse(JSON.stringify(nestedSession)) : null,
          flows: nestedFlows.map(f => JSON.parse(JSON.stringify(f))),
        };
        // ADO #244 Phase 2.5 — queue backend deletes for the nested session
        // and all its UUID-owning flows.
        const isUuid = (id) => typeof id === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(id);
        const pendingFlowDeletes = [...get()._pendingFlowDeletes, ...nestedFlows.map(f => f.id).filter(isUuid)];
        const pendingSessionDeletes = [...get()._pendingSessionDeletes, ...(isUuid(node._sessionId) ? [node._sessionId] : [])];
        set({ flows: remainingFlows, sessions: remainingSessions, _implFlowTrash: trash, _pendingFlowDeletes: pendingFlowDeletes, _pendingSessionDeletes: pendingSessionDeletes });
        pushToHistory();
        return;
      }
    }

    const updatedNodes = findAndDeleteNode(flow.nodes, nodeId);
    set({
      flows: flows.map(f => f.id === activeFlowId ? { ...f, nodes: updatedNodes } : f)
    });
    pushToHistory();
  },
  
  // Undo for ACTIVE FLOW (per-flow undo)
  undo: () => {
    const { flows, activeFlowId } = get();
    const flowIndex = flows.findIndex(f => f.id === activeFlowId);
    if (flowIndex === -1) return false;
    
    const flow = flows[flowIndex];
    
    if (flow.historyIndex > 0) {
      const newIndex = flow.historyIndex - 1;
      const state = JSON.parse(flow.history[newIndex]);

      let newFlows = [...flows];
      newFlows[flowIndex] = {
        ...flow,
        nodes: state.nodes,
        resources: state.resources,
        historyIndex: newIndex,
        skipHistoryPush: false
      };
      // ADO #244 Phase 0 — re-attach impl flows / nested sessions for any
      // contract nodes that came back via this undo.
      const { _implFlowTrash, sessions } = get();
      let newSessions = sessions;
      const newTrash = { ..._implFlowTrash };
      // ADO #244 Phase 2.5 — track ids that come back so we can drop them
      // from the pending-delete queues (undo cancels the deletion).
      const restoredFlowIds = new Set();
      const restoredSessionIds = new Set();
      const walk = (ns) => {
        for (const n of (ns || [])) {
          const t = newTrash[n.id];
          if (n._implFlowId && t?.kind === 'impl' && !newFlows.find(f => f.id === n._implFlowId)) {
            // Trash stores all owned flows (1 for normal, up to 3 for @property)
            for (const f of (t.flows || [])) {
              if (!newFlows.find(x => x.id === f.id)) newFlows.push(f);
              restoredFlowIds.add(f.id);
            }
            delete newTrash[n.id];
          }
          if (n._sessionId && t?.kind === 'session' && !newSessions.find(s => s.id === n._sessionId)) {
            newSessions = [...newSessions, t.session].filter(Boolean);
            if (t.session) restoredSessionIds.add(t.session.id);
            for (const f of t.flows) {
              if (!newFlows.find(x => x.id === f.id)) newFlows.push(f);
              restoredFlowIds.add(f.id);
            }
            delete newTrash[n.id];
          }
        }
      };
      walk(state.nodes);
      const { _pendingFlowDeletes, _pendingSessionDeletes } = get();
      const nextPendingFlowDeletes = (_pendingFlowDeletes || []).filter(id => !restoredFlowIds.has(id));
      const nextPendingSessionDeletes = (_pendingSessionDeletes || []).filter(id => !restoredSessionIds.has(id));
      set({ flows: newFlows, sessions: newSessions, _implFlowTrash: newTrash, _pendingFlowDeletes: nextPendingFlowDeletes, _pendingSessionDeletes: nextPendingSessionDeletes });
      return true;
    }
    return false;
  },

  // Redo for ACTIVE FLOW (per-flow redo)
  redo: () => {
    const { flows, activeFlowId, sessions } = get();
    const flowIndex = flows.findIndex(f => f.id === activeFlowId);
    if (flowIndex === -1) return false;

    const flow = flows[flowIndex];

    if (flow.historyIndex < flow.history.length - 1) {
      const newIndex = flow.historyIndex + 1;
      const state = JSON.parse(flow.history[newIndex]);

      // ADO #244 Phase 0 — detect contract nodes being re-deleted by redo
      // (present in current state.nodes, absent in next). Stash their impl
      // artifacts so a subsequent undo can put them back again.
      const prevNodes = flow.nodes || [];
      const nextNodes = state.nodes || [];
      const nextIds = new Set(nextNodes.map(n => n.id));
      const removedNodes = prevNodes.filter(n => !nextIds.has(n.id));

      let newFlows = [...flows];
      let newSessions = sessions;
      const newTrash = { ...get()._implFlowTrash };
      for (const n of removedNodes) {
        if (n._implFlowId) {
          const ownedIds = n._propertyFlowIds && n._propertyFlowIds.length > 0
            ? n._propertyFlowIds
            : [n._implFlowId];
          const ownedFlows = newFlows.filter(f => ownedIds.includes(f.id));
          if (ownedFlows.length > 0) {
            newTrash[n.id] = { kind: 'impl', flows: ownedFlows.map(f => JSON.parse(JSON.stringify(f))) };
            newFlows = newFlows.filter(f => !ownedIds.includes(f.id));
          }
        }
        if (n._sessionId) {
          const sess = newSessions.find(s => s.id === n._sessionId);
          const nested = newFlows.filter(f => f.sessionId === n._sessionId);
          newTrash[n.id] = {
            kind: 'session',
            session: sess ? JSON.parse(JSON.stringify(sess)) : null,
            flows: nested.map(f => JSON.parse(JSON.stringify(f))),
          };
          newFlows = newFlows.filter(f => f.sessionId !== n._sessionId);
          newSessions = newSessions.filter(s => s.id !== n._sessionId);
        }
      }

      newFlows[flowIndex] = {
        ...flow,
        nodes: state.nodes,
        resources: state.resources,
        historyIndex: newIndex,
        skipHistoryPush: false
      };
      set({ flows: newFlows, sessions: newSessions, _implFlowTrash: newTrash });
      return true;
    }
    return false;
  },
  
  // Check undo/redo availability for ACTIVE FLOW
  canUndo: () => {
    const flow = get().getActiveFlow();
    return flow ? flow.historyIndex > 0 : false;
  },
  
  canRedo: () => {
    const flow = get().getActiveFlow();
    return flow ? flow.historyIndex < flow.history.length - 1 : false;
  },
  
  // Utilities
  getNodeById: (nodeId) => {
    const flow = get().getActiveFlow();
    return flow ? findNodeById(flow.nodes, nodeId) : null;
  },
  
  getNodeCount: () => {
    const flow = get().getActiveFlow();
    return flow ? countNodes(flow.nodes) : 0;
  },
  
  // Per-flow Clipboard Operations (like Google Sheets - per tab)
  cutNodeToClipboard: (node) => {
    const { flows, activeFlowId } = get();
    set({
      flows: flows.map(f => f.id === activeFlowId 
        ? { 
            ...f, 
            clipboard: JSON.parse(JSON.stringify(node)), 
            clipboardAction: 'cut',
            cutNodeId: node.id
          } 
        : f
      )
    });
  },
  
  copyNodeToClipboard: (node) => {
    const { flows, activeFlowId } = get();
    set({
      flows: flows.map(f => f.id === activeFlowId 
        ? { 
            ...f, 
            clipboard: JSON.parse(JSON.stringify(node)), 
            clipboardAction: 'copy',
            cutNodeId: null
          } 
        : f
      )
    });
  },
  
  clearFlowClipboard: () => {
    const { flows, activeFlowId } = get();
    set({
      flows: flows.map(f => f.id === activeFlowId 
        ? { ...f, clipboard: null, clipboardAction: null, cutNodeId: null } 
        : f
      )
    });
  },
  
  // Get clipboard from active flow
  getClipboard: () => {
    const flow = get().getActiveFlow();
    return flow?.clipboard || null;
  },
  
  getClipboardAction: () => {
    const flow = get().getActiveFlow();
    return flow?.clipboardAction || null;
  },
  
  getCutNodeId: () => {
    const flow = get().getActiveFlow();
    return flow?.cutNodeId || null;
  },
  
  hasClipboard: () => {
    const flow = get().getActiveFlow();
    return flow?.clipboard !== null;
  },
  
  // Paste node after target (uses clipboard from active flow)
  pasteNode: (afterId) => {
    const { flows, activeFlowId, addNode, pushToHistory } = get();
    const flow = flows.find(f => f.id === activeFlowId);
    if (!flow?.clipboard) return null;
    
    const clipboardNode = flow.clipboard;
    const clipboardAction = flow.clipboardAction;
    
    // Create new node from clipboard
    const newNode = addNode(afterId, clipboardNode.type);
    
    if (newNode) {
      // Copy properties from clipboard node (except id). ADO #582 — every
      // nested node (and outcome / branch) gets a fresh id; sharing them with
      // the original made deleting a child of the copy delete both.
      const { updateNode } = get();
      const updates = reIdNodeTree(clipboardNode);
      delete updates.id;
      delete updates.type;
      
      // Generate new label for copied node
      if (clipboardAction === 'copy') {
        updates.label = `Copy of ${clipboardNode.label || clipboardNode.type}`;
      } else {
        updates.label = clipboardNode.label;
      }
      
      updateNode(newNode.id, updates);
      
      // Clear clipboard if it was a cut action
      if (clipboardAction === 'cut') {
        get().clearFlowClipboard();
      }
    }
    
    return newNode;
  },
  
  // Load project from backend
  // ADO #516 — after DELETE /projects/{id}/includes/{fileId}: forget the
  // library's file, sessions, flows and shared resources. Nothing is saved:
  // none of it was ever this project's.
  dropIncluded: (fileId) => {
    const { files, sessions, flows, globalResources, activeFlowId } = get();
    const gone = (x) => x.includedFrom && x.includedFrom.fileId === fileId;
    const keptFlows = flows.filter((f) => !gone(f));
    set({
      files: files.filter((f) => !gone(f)),
      sessions: sessions.filter((s) => !gone(s)),
      flows: keptFlows,
      globalResources: (globalResources || []).filter((r) => !gone(r)),
      includes: (get().includes || []).filter((i) => i.fileId !== fileId),
      ...(keptFlows.some((f) => f.id === activeFlowId) ? {} : { activeFlowId: (keptFlows.find((f) => f.isEntry) || keptFlows[0] || {}).id || null }),
    });
  },

  // ADO #516 — after an include is added: take the included items from a
  // fresh load and put them in place of the old ones, leaving everything of
  // this project's (saved or not) as it is.
  replaceIncluded: (fresh) => {
    const inc = (x) => !!x.includedFrom;
    const { files, sessions, flows, globalResources } = get();
    const newFiles = (fresh.files || []).filter(inc).map((f) => ({
      ...f, fileClassType: f.fileClassType || f.file_class_type || 'class',
      recordComponents: f.recordComponents || f.record_components || [],
    }));
    const newSessions = (fresh.sessions || []).filter(inc);
    const newFlows = (fresh.flows || []).filter(inc).map((flow) => {
      if (flow.sessionId) return flow;
      const file = newFiles.find((f) => f.name === flow.fileName);
      const primary = file && newSessions.find((x) => x.fileId === file.id && !x.parentSessionId);
      return primary ? { ...flow, sessionId: primary.id } : flow;
    });
    set({
      files: [...files.filter((f) => !inc(f)), ...newFiles],
      sessions: [...sessions.filter((x) => !inc(x)), ...newSessions],
      flows: [...flows.filter((f) => !inc(f)), ...newFlows],
      globalResources: [...(globalResources || []).filter((r) => !inc(r)), ...(fresh.globalResources || []).filter(inc)],
      includes: fresh.includes || [],
    });
  },

  loadProject: (projectData) => {
    const { projectId, projectName, globalResources, language: projectLanguage } = projectData;
    // ADO #583 — a project several items share keeps its Contract in view: a
    // capstone (its client panel says so) or one whose key is not the item
    // that created it (My Toolbox).
    const projectKey = projectData.projectKey || projectData.project_key || null;
    const createdBy = projectData.practiceItemSlug || projectData.practice_item_slug || null;
    const projectShared = !!(projectData.client_panel || projectData.clientPanel)
      || !!(projectKey && createdBy && projectKey !== createdBy);
    // ADO #223 — migrate legacy flows to the new schema on load
    let flows = (projectData.flows || []).map(migrateFlowSchema);
    // ADO #516 — an included library's flows are set aside before any healing
    // below and put back at the end. Healing rewrites, synthesises and DROPS
    // flows (dropped ones are deleted on the next save), and none of that may
    // touch a library: for the learner's own toolbox, the server would accept it.
    const includedFlows = flows.filter((f) => f.includedFrom);
    flows = flows.filter((f) => !f.includedFrom);
    // Restore multi-file state if present.
    // ADO #229 — normalise file_class_type + record_components for each file.
    // Backend returns snake_case; the frontend uses camelCase.
    const files = (projectData.files || []).map(f => ({
      ...f,
      fileClassType: f.fileClassType || f.file_class_type || 'class',
      recordComponents: f.recordComponents || f.record_components || [],
    }));
    // ADO #237 — sessions arrive already in camelCase from sessionFromBackend.
    // For legacy single-file projects (no files[]), sessions[] is empty and
    // flows have sessionId === null; the implicit-session path covers them.
    const sessions = projectData.sessions || [];

    // ADO #244 Phase 0/2.2 — Legacy projects (created before multi-file) have
    // no files[] and no sessions[]. Synthesize a default file + session so
    // the contract-flow synthesizer below has something to attach to.
    //
    // CRITICAL: flows that were saved with a fileName (e.g. "main.py") need
    // a synth file with that EXACT name, otherwise the fileName-binding
    // pass below misses → flows orphan → contract synthesizer fires fresh
    // → user thinks their saved Contract Flow disappeared. So we prefer an
    // existing flow.fileName over inventing one from the project name.
    if (files.length === 0 && flows.length > 0) {
      const ext = { c: '.c', cpp: '.cpp', java: '.java', python: '.py', javascript: '.js' }[projectLanguage] || '';
      const projectBase = (projectName || 'main').replace(/[^a-zA-Z0-9_]/g, '') || 'main';
      const knownFileName = flows.find(f => f.fileName)?.fileName;
      const synthFileName = knownFileName || `${projectBase}${ext}`;
      const synthSessionName = synthFileName.replace(/\.(java|c|cpp|py|h)$/i, '');
      const synthFile = {
        id: `file-synth-${Date.now()}`,
        name: synthFileName,
        fileClassType: 'class',
        recordComponents: [],
      };
      const synthSession = {
        id: `session-synth-${synthFile.id}`,
        fileId: synthFile.id,
        parentSessionId: null,
        kind: 'class',
        name: synthSessionName,
        accessSpecifier: 'public',
        sessionData: {},
        displayOrder: 0,
      };
      files.push(synthFile);
      sessions.push(synthSession);
      // Bind dangling flows to the synthesized session.
      flows.forEach(f => {
        if (!f.fileName) f.fileName = synthFile.name;
        if (!f.sessionId) f.sessionId = synthSession.id;
      });
    }

    // ADO #240 — ensure every multi-file project file has a primary session.
    // When the backend hasn't deployed the session routes yet (Railway on
    // main), no sessions come back. Synthesize one per file so the store
    // always has real session objects — the outline, FlowTabs, and codegen
    // all rely on sessions[] being populated.
    if (files.length > 0) {
      files.forEach(file => {
        const hasSession = sessions.some(s => s.fileId === file.id);
        if (!hasSession) {
          const fct = file.fileClassType || 'class';
          // ADO #244 Phase 3d — .h/.hpp synth as header session, not class.
          // ADO #244 Phase 3e — fileClassType 'freeFunctions' round-trips.
          const isHeader = /\.(h|hpp|hh|hxx)$/i.test(file.name || '');
          const synthKind =
            isHeader ? 'header' :
            fct === 'freeFunctions' ? 'freeFunctions' :
            fct === 'record' ? 'record' :
            fct === 'interface' ? 'interface' :
            fct === 'enum' ? 'enum' :
            'class';
          sessions.push({
            id: `session-synth-${file.id}`,
            fileId: file.id,
            parentSessionId: null,
            kind: synthKind,
            name: (file.name || '').replace(/\.(java|c|cpp|py|h|hpp|hh|hxx)$/i, ''),
            accessSpecifier: 'public',
            sessionData: fct === 'record' ? { recordComponents: file.recordComponents || [] } : {},
            displayOrder: 0,
            // ADO #516 — a session made up for an included file is as
            // read-only as the file: never posted to the library.
            ...(file.includedFrom ? { includedFrom: file.includedFrom, readOnly: true } : {}),
          });
        }
      });
    }

    // ADO #237 hotfix — backfill flow.sessionId from flow.fileName for
    // legacy multi-file projects whose flows had file_id NULL (so the
    // database backfill missed them). Without this, FlowTabs filters by
    // sessionId and shows zero flows.
    if (sessions.length > 0) {
      flows = flows.map(flow => {
        if (flow.sessionId) return flow;
        if (!flow.fileName) return flow;
        const matchingFile = files.find(f => f.name === flow.fileName);
        if (!matchingFile) return flow;
        const primarySession = sessions.find(s => s.fileId === matchingFile.id && !s.parentSessionId);
        if (!primarySession) return flow;
        return { ...flow, sessionId: primarySession.id };
      });
    }

    // ADO #267 follow-up — heal-on-load: drop orphan phantom main flows.
    // The session-based dedup below misses these because it filters by
    // sessionId (orphans have sessionId=null). See _isOrphanPhantomFlow
    // helper for the predicate + rationale.
    const _orphanIds = [];
    flows = flows.filter((flow) => {
      if (!_isOrphanPhantomFlow(flow, flows)) return true;
      _orphanIds.push(flow.id);
      return false;
    });

    // ADO #244 Phase 2.3 — dedup duplicate entry "main" flows per session.
    // Backend creates a default main on project create; the first addFile
    // call also creates a main bound to the new file. Result: two
    // is_entry=true main flows in the DB, both surface as separate tabs
    // on reload. Keep the one with the most user content (most non
    // start/end nodes); drop the rest. Safe because empty mains have
    // never been touched by the student.
    if (sessions.length > 0) {
      sessions.forEach(session => {
        const entryMains = flows.filter(f => f.sessionId === session.id && f.isEntry && (f.flowType === 'main' || !f.flowType));
        if (entryMains.length <= 1) return;
        // Pick the most-content flow as the survivor; ties broken by latest position.
        const score = (f) => (f.nodes || []).filter(n => n.type !== 'start' && n.type !== 'end').length;
        const survivor = entryMains.reduce((best, cur) => score(cur) > score(best) ? cur : best);
        const toDrop = new Set(entryMains.filter(f => f.id !== survivor.id).map(f => f.id));
        flows = flows.filter(f => !toDrop.has(f.id));
      });
    }

    // ADO #580 — heal-on-load: a C project's entry. A non-contract flow
    // NAMED main is the entry whatever it is marked (codegen's rule too, see
    // resolveEntryFlow). Ram's p-txt-des-first-statement project held no
    // entry at all: indexOf (made with the Contract) and an ordinary void
    // `main`; Run built indexOf as main plus a second `void main`. Also heals
    // a flow saved with flowType 'main' whose is_entry the old update path
    // never sent. The healed flows are marked dirty so the next save writes
    // them (is_entry rides on the update now, see saveProjectState).
    const healedEntryIds = new Set();
    let healedEntryId = null;
    const demotedEntryIds = new Set();
    {
      const isCProject = projectLanguage
        ? projectLanguage === 'c'
        : [...files.map((f) => f.name), ...flows.map((f) => f.fileName)].some((n) => /\.c$/i.test(n || ''));
      if (isCProject) {
        const own = flows.filter((f) => f.flowType !== 'contract');
        const named = own.filter((f) => f.name === 'main');
        const marked = own.filter(isEntryFlow);
        const entry = named.find(_flowHasContent) || named[0]
          || marked.find((f) => f.isEntry === true) || marked[0] || null;
        if (entry) {
          flows = flows.map((f) => {
            if (f.flowType === 'contract') return f;
            if (f === entry) {
              if (f.isEntry === true && f.flowType === 'main') return f;
              healedEntryIds.add(f.id);
              healedEntryId = f.id;
              return { ...f, isEntry: true, flowType: 'main', returnType: 'int' };
            }
            // Another flow flagged entry beside a flow named main: a function.
            if (named.length && entry.name === 'main' && f.name !== 'main' && isEntryFlow(f)) {
              healedEntryIds.add(f.id);
              demotedEntryIds.add(f.id);
              return { ...f, isEntry: false, flowType: 'function' };
            }
            return f;
          });
        }
      }
    }

    // ADO #243 — heal orphan contract-content flows.
    // In earlier dev iterations, contract flows could round-trip with the
    // wrong flowType (e.g. 'static') because flowType wasn't always
    // persisted. The synthesizer below would then not find a contract flow
    // and create a fresh one, leaving the old one as a stale duplicate.
    // Detect a flow whose nodes are exactly [configuration, resources, end]
    // and force its flowType back to 'contract' so synthesis is idempotent.
    flows = flows.map(flow => {
      if (flow.flowType === 'contract') return flow;
      const types = (flow.nodes || []).map(n => n.type);
      const looksLikeContract =
        types.length >= 2 &&
        types.includes('configuration') &&
        types.includes('resources') &&
        !types.some(t => t !== 'configuration' && t !== 'resources' && t !== 'end');
      return looksLikeContract ? { ...flow, flowType: 'contract' } : flow;
    });

    // ADO #242 — synthesize a Contract Flow tab for every session that
    // doesn't already have one. Existing projects pre-date Contract Flow
    // so all their flows are implementation flows.
    if (sessions.length > 0) {
      sessions.forEach(session => {
        // ADO #516 — an included library brings its own Contract; one made up
        // here would be saved into THIS project under the library's file name.
        if (session.includedFrom) return;
        const hasContract = flows.some(f => f.sessionId === session.id && f.flowType === 'contract');
        if (!hasContract) {
          const file = files.find(f => f.id === session.fileId);
          // ADO #266 — seed a function_decl for the entry function when
          // synthesizing main.c's contract. Mirrors the bootstrap-second-
          // file path; without this, projects loaded from DB never get the
          // entry decl until they're recreated.
          const sessionEntry = file
            ? flows.find(f => f.fileName === file.name && f.isEntry && f.flowType !== 'contract')
            : null;
          const cf = createContractFlow(session.name, sessionEntry ? {
            entryImplFlowId: sessionEntry.id,
            entryFunctionName: sessionEntry.name || 'main',
            entryReturnType: 'int',
            entryParameters: [],
          } : {});
          cf.sessionId = session.id;
          if (file) cf.fileName = file.name;
          // ADO #577 — a practice starter ships ready-made subflows (e.g.
          // `display` in "Hand it over") with no Contract. Declare them here as
          // main is declared above, or the orphan cleanup below (#246) hides
          // them and the next save deletes them. Only when the session has NO
          // Contract, so a saved Contract is never second-guessed.
          // In a C free-functions file an untyped flow migrates to the legacy
          // 'static' (p-prog-main-welcome's `welcome`): a function all the same.
          const starterFns = flows.filter(f =>
            f.sessionId === session.id && f !== sessionEntry && !f.isEntry
            && (f.flowType === 'function' || (f.flowType === 'static' && _isFreeFunctionsSession(session, (file && file.name) || f.fileName))));
          if (starterFns.length) {
            const endIdx = cf.nodes.findIndex(n => n.type === 'end');
            const decls = starterFns.map((f, i) => buildEntryFunctionDecl({
              entryImplFlowId: f.id,
              entryFunctionName: f.name,
              entryReturnType: f.returnType || 'void',
              entryParameters: (f.parameters || []).map(p => ({ name: p.name, type: p.type })),
            }, `${Date.now()}-${i}`));
            cf.nodes.splice(endIdx >= 0 ? endIdx : cf.nodes.length, 0, ...decls);
          }
          // Re-snapshot history when an entry decl is auto-included so the
          // baseline reflects the seeded node.
          if (sessionEntry || starterFns.length) {
            cf.history = [JSON.stringify({ nodes: cf.nodes, resources: cf.resources })];
          }
          flows.push(cf);
        }
      });
    }

    // Heal-on-load: every Contract flow has its Data Members (resources) node,
    // right after Configuration, as createContractFlow makes it. The canvas
    // draws only what is saved, and the node cannot be added back from the UI,
    // so a Contract flow saved without one (imported, older, or written by a
    // script — the mini-Postgres engine's were) left its file nowhere to list
    // or add its own data (Ram, 2026-09-30).
    flows = flows.map(flow => {
      if (flow.flowType !== 'contract') return flow;
      const nodes = flow.nodes || [];
      if (nodes.some(n => n.type === 'resources')) return flow;
      const cfgIdx = nodes.findIndex(n => n.type === 'configuration');
      const healed = [...nodes];
      healed.splice(cfgIdx >= 0 ? cfgIdx + 1 : 0, 0,
        { id: `resources-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, type: 'resources', label: 'Data Members' });
      return { ...flow, nodes: healed,
        history: [JSON.stringify({ nodes: healed, resources: flow.resources || [] })], historyIndex: 0 };
    });

    // ADO #266 backfill — projects whose Contract already exists (so the
    // synthesizer above skips them) but are missing the entry function_decl.
    // Add it just before End so the contract surface is complete.
    flows.forEach(contractFlow => {
      if (contractFlow.flowType !== 'contract') return;
      if (!contractFlow.fileName) return;
      const entryFlow = flows.find(f =>
        f.fileName === contractFlow.fileName
        && f.isEntry
        && f.flowType !== 'contract'
      );
      if (!entryFlow) return;
      // ADO #445 — the entry (main) decl belongs ONLY to the entry's OWN
      // session contract. A Python module holds several class contracts in one
      // file, and this fileName-only match used to stamp main() onto every one
      // of them (a stray "Function: main()" inside class Dog's contract). When
      // sessions are known, require the contract to be the entry's session.
      const sessionsKnown = !!entryFlow.sessionId && !!contractFlow.sessionId;
      const isEntrySessionContract = !sessionsKnown || entryFlow.sessionId === contractFlow.sessionId;
      const entryDeclIdx = (contractFlow.nodes || []).findIndex(
        n => n.type === 'function_decl'
          && (n._implFlowId === entryFlow.id || n.functionName === entryFlow.name)
      );

      if (!isEntrySessionContract) {
        // Heal projects where an earlier load/save baked main() into a
        // non-entry class contract — drop it.
        if (entryDeclIdx >= 0) {
          contractFlow.nodes.splice(entryDeclIdx, 1);
          contractFlow.history = [JSON.stringify({
            nodes: contractFlow.nodes,
            resources: contractFlow.resources,
          })];
          contractFlow.historyIndex = 0;
        }
        return;
      }

      if (entryDeclIdx >= 0) return; // entry contract already declares main
      const decl = buildEntryFunctionDecl({
        entryImplFlowId: entryFlow.id,
        entryFunctionName: entryFlow.name || 'main',
        entryReturnType: 'int',
        entryParameters: [],
      }, Date.now());
      const endIdx = (contractFlow.nodes || []).findIndex(n => n.type === 'end');
      if (endIdx >= 0) {
        contractFlow.nodes.splice(endIdx, 0, decl);
      } else {
        contractFlow.nodes.push(decl);
      }
      // History rebaseline — the user's first edit shouldn't be able to
      // undo past the auto-seeded decl into a "broken contract" state.
      contractFlow.history = [JSON.stringify({
        nodes: contractFlow.nodes,
        resources: contractFlow.resources,
      })];
      contractFlow.historyIndex = 0;
    });

    // ADO #580 — the Contract agrees with the healed entry: its main is
    // declared int, and a flow demoted from entry is declared as the function
    // it now is (else the orphan cleanup below would delete it).
    if (healedEntryId || demotedEntryIds.size) {
      flows = flows.map((cf) => {
        if (cf.flowType !== 'contract') return cf;
        let nodes = cf.nodes || [];
        let changed = false;
        if (healedEntryId) {
          nodes = nodes.map((n) => {
            if (n.type !== 'function_decl' || n._implFlowId !== healedEntryId || n.returnType === 'int') return n;
            changed = true;
            return { ...n, returnType: 'int' };
          });
        }
        demotedEntryIds.forEach((id) => {
          const f = flows.find((x) => x.id === id);
          if (!f || f.sessionId !== cf.sessionId) return;
          if (nodes.some((n) => n._implFlowId === id)) return;
          const decl = buildEntryFunctionDecl({
            entryImplFlowId: id,
            entryFunctionName: f.name,
            entryReturnType: f.returnType || 'void',
            entryParameters: (f.parameters || []).map((p) => ({ name: p.name, type: p.type })),
          }, `${Date.now()}-${id}`);
          const endIdx = nodes.findIndex((n) => n.type === 'end');
          nodes = [...nodes];
          nodes.splice(endIdx >= 0 ? endIdx : nodes.length, 0, decl);
          changed = true;
        });
        if (!changed) return cf;
        healedEntryIds.add(cf.id);
        return { ...cf, nodes, history: [JSON.stringify({ nodes, resources: cf.resources || [] })], historyIndex: 0 };
      });
    }

    // ADO #246 — orphan impl-flow cleanup.
    // A contract node's _implFlowId / _propertyFlowIds pointer is the
    // single source of truth for what impl flows "belong to" a session.
    // Cycles of add→save→cascade-delete in pre-#244 projects sometimes
    // left impl flows in the DB that no surviving declaration references.
    // On load, drop any impl flow whose id isn't reachable from a contract
    // node in the same session. Queue its id for backend delete on next
    // save (if it's a UUID). Configuration/main/nested-session flows are
    // intentionally excluded — they don't have a contract-node owner.
    const IMPL_FLOW_TYPES = new Set([
      'constructor', 'compactConstructor', 'staticInitializer', 'destructor',
      'instance', 'static', 'staticMethod', 'defaultMethod', 'classMethod',
      'dunderMethod', 'propertyGetter', 'propertySetter', 'propertyDeleter',
      'operator', 'function', 'accessor',
    ]);
    const uuidRe = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    const droppedBackendIds = [];
    // ADO #267 follow-up — queue any persisted orphan phantom rows for
    // backend cleanup so the next save erases them from the DB.
    _orphanIds.forEach((id) => { if (uuidRe.test(id)) droppedBackendIds.push(id); });
    if (sessions.length > 0) {
      // A function in a .c file saved as kind 'class' never got its Contract
      // card (New Flow skipped it): declare it now rather than drop it as an
      // orphan. Only there — in a freeFunctions file an undeclared flow is
      // one whose card was deleted, and stays an orphan.
      const classKindC = new Set(sessions.filter((x) => x.kind && x.kind !== 'freeFunctions').map((x) => x.id));
      flows.filter((f) => IMPL_FLOW_TYPES.has(f.flowType) && !f.isEntry && classKindC.has(f.sessionId) && /\.c$/i.test(f.fileName || ''))
        .forEach((f) => { flows = _declareOnContract(flows, f, sessions).flows; });
      // Collect the set of impl-flow ids referenced by surviving contract
      // nodes, grouped by the session they live in.
      const referencedBySession = new Map(); // sessionId → Set<flowId>
      flows.forEach(flow => {
        if (flow.flowType !== 'contract') return;
        const refs = new Set();
        (flow.nodes || []).forEach(n => {
          if (n._implFlowId) refs.add(n._implFlowId);
          if (Array.isArray(n._propertyFlowIds)) n._propertyFlowIds.forEach(id => refs.add(id));
        });
        referencedBySession.set(flow.sessionId, refs);
      });
      flows = flows.filter(flow => {
        if (!IMPL_FLOW_TYPES.has(flow.flowType)) return true;
        const refs = referencedBySession.get(flow.sessionId);
        if (!refs) return true; // no contract in this session yet; leave alone
        if (refs.has(flow.id)) return true;
        // Orphan — drop + queue backend delete if it's a real row.
        if (uuidRe.test(flow.id)) droppedBackendIds.push(flow.id);
        return false;
      });
    }

    // ADO #449 — reconcile the variadic flag from each function_decl node onto
    // its impl flow. The checkbox state persists on the contract node, but a
    // project saved before flow.isVariadic was mirrored (or where the field
    // didn't round-trip) would open with the box checked yet codegen missing
    // the `...`. Deriving it here from the source-of-truth node makes load
    // idempotent — same fix the updateNode sync applies on edit.
    {
      const variadicByImplId = new Map();
      flows.forEach(flow => {
        if (flow.flowType !== 'contract') return;
        (flow.nodes || []).forEach(n => {
          if (n.type === 'function_decl' && n._implFlowId) {
            variadicByImplId.set(n._implFlowId, !!n.isVariadic);
          }
        });
      });
      if (variadicByImplId.size > 0) {
        flows = flows.map(f =>
          variadicByImplId.has(f.id) ? { ...f, isVariadic: variadicByImplId.get(f.id) } : f
        );
      }
    }

    const activeFileId = files.length > 0 ? files[0].id : null;
    const activeSessionId = activeFileId
      ? (sessions.find(s => s.fileId === activeFileId && !s.parentSessionId)?.id || null)
      : null;
    // ADO #244 Phase 0 — always prefer the active session's Contract Flow as
    // the default tab. Falls back to the first flow in the file, then to
    // flows[0]. Pedagogy: declaration first, implementation next.
    let firstFlowId = flows[0]?.id || null;
    const contractFlow = activeSessionId
      ? flows.find(f => f.sessionId === activeSessionId && f.flowType === 'contract')
      : flows.find(f => f.flowType === 'contract');
    // ADO #583 — a Contract that declares nothing but main is hidden; the
    // item opens on main() instead.
    const contractHidden = contractFlow && isContractHidden(contractFlow, {
      flows, files, sessions, globalResources: globalResources || [], projectShared,
      contractPinned: false, activeFlowId: null,
    });
    const entryOfSession = contractHidden
      ? flows.find(f => f.sessionId === contractFlow.sessionId && isEntryFlow(f))
      : null;
    if (entryOfSession) {
      firstFlowId = entryOfSession.id;
    } else if (contractFlow) {
      firstFlowId = contractFlow.id;
    } else if (files.length > 0) {
      const firstFileFlows = flows.filter(f => f.fileName === files[0].name);
      firstFlowId = firstFileFlows[0]?.id || flows[0]?.id || null;
    }
    // ADO #516 — the included flows come back, untouched but for a session to
    // show them under (FlowTabs lists a session's flows).
    flows = [...flows, ...includedFlows.map((flow) => {
      if (flow.sessionId) return flow;
      const file = files.find((f) => f.name === flow.fileName && f.includedFrom);
      const primary = file && sessions.find((x) => x.fileId === file.id && !x.parentSessionId);
      return primary ? { ...flow, sessionId: primary.id } : flow;
    })];
    const keepIds = new Set(includedFlows.map((f) => f.id));
    const pendingDeletes = droppedBackendIds.filter((id) => !keepIds.has(id));

    set({
      projectId,
      projectName,
      flows,
      files,
      sessions,
      includes: projectData.includes || [],
      activeFileId,
      activeSessionId,
      globalResources: globalResources || [],
      activeFlowId: firstFlowId,
      hasUnsavedChanges: droppedBackendIds.length > 0 || healedEntryIds.size > 0,
      // ADO #274 — fresh load resets the dirty set; flag meta-dirty
      // only when reconciliation dropped orphan backend ids (those
      // need a follow-up save to persist the prune).
      // ADO #580 — flows the entry heal rewrote are dirty: the next save
      // persists them.
      dirtyFlowIds: new Set([...healedEntryIds].filter((id) => flows.some((f) => f.id === id))),
      projectLanguage: projectLanguage || null,
      projectShared,
      contractPinned: false,
      projectMetaDirty: droppedBackendIds.length > 0,
      lastSavedAt: new Date().toISOString(),
      // Reset global history
      globalHistory: [JSON.stringify(globalResources || [])],
      globalHistoryIndex: 0,
      // ADO #246 — orphans queued for backend delete on next save.
      _pendingFlowDeletes: pendingDeletes,
      _pendingSessionDeletes: [],
      _implFlowTrash: {},
    });
  },
  
  // ADO #267 follow-up — heal-on-save: identify and queue orphan phantom
  // main flows for deletion before persisting. Mirrors heal-on-load so a
  // brand-new orphan (e.g. backend default main row created by the
  // createProject endpoint) gets cleaned on the next save instead of
  // surviving until the next reload. UUID orphans queue into
  // _pendingFlowDeletes so the save action's drainPendingDeletes pass
  // issues the backend delete.
  pruneOrphanFlows: () => {
    const { flows, _pendingFlowDeletes } = get();
    const isUuid = (id) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
    const orphans = flows.filter((f) => _isOrphanPhantomFlow(f, flows));
    if (orphans.length === 0) return 0;
    const orphanIds = new Set(orphans.map((f) => f.id));
    const remainingFlows = flows.filter((f) => !orphanIds.has(f.id));
    const newPending = [
      ...(_pendingFlowDeletes || []),
      ...orphans.map((f) => f.id).filter(isUuid),
    ];
    set({ flows: remainingFlows, _pendingFlowDeletes: newPending });
    return orphans.length;
  },

  // ADO #244 Phase 2.5 — return + clear queued backend deletes from cascade.
  // Caller (save path) is responsible for invoking deleteFlow / deleteSession.
  drainPendingDeletes: () => {
    const { _pendingFlowDeletes, _pendingSessionDeletes } = get();
    const out = {
      flowIds: [...(_pendingFlowDeletes || [])],
      sessionIds: [...(_pendingSessionDeletes || [])],
    };
    set({ _pendingFlowDeletes: [], _pendingSessionDeletes: [] });
    return out;
  },

  // Update flow IDs after save (when new flows get backend IDs).
  // ADO #244 Phase 2.4 — also remap _implFlowId / _propertyFlowIds links
  // stored on contract nodes (constructor_decl, method_decl, function_decl,
  // etc.) and the trash map keyed by deleted contract node ids. Without
  // this, deleting a saved contract node fails to cascade because
  // node._implFlowId still points at the old frontend id.
  updateFlowIds: (idMappings) => {
    const { flows, activeFlowId, _implFlowTrash } = get();
    let newActiveFlowId = activeFlowId;
    const idMap = new Map(idMappings.map(m => [m.oldId, m.newId]));
    const remap = (id) => idMap.get(id) || id;

    const remapNode = (n) => {
      let next = n;
      if (n._implFlowId && idMap.has(n._implFlowId)) {
        next = { ...next, _implFlowId: idMap.get(n._implFlowId) };
      }
      if (Array.isArray(n._propertyFlowIds) && n._propertyFlowIds.some(id => idMap.has(id))) {
        next = { ...next, _propertyFlowIds: n._propertyFlowIds.map(remap) };
      }
      return next;
    };

    const updatedFlows = flows.map(flow => {
      let next = flow;
      // Remap flow's own id
      const mapping = idMappings.find(m => m.oldId === flow.id);
      if (mapping) {
        if (activeFlowId === flow.id) newActiveFlowId = mapping.newId;
        next = { ...next, id: mapping.newId };
      }
      // Remap node references inside this flow
      if (Array.isArray(next.nodes) && next.nodes.some(n => n._implFlowId || n._propertyFlowIds)) {
        next = { ...next, nodes: next.nodes.map(remapNode) };
      }
      return next;
    });

    // Trash entries are keyed by contract-node id (stable across save) but
    // their stored flows carry old ids — remap those too so undo restores
    // with the backend id, not the dead frontend one.
    const updatedTrash = {};
    for (const [k, entry] of Object.entries(_implFlowTrash || {})) {
      if (entry?.kind === 'impl' && Array.isArray(entry.flows)) {
        updatedTrash[k] = { ...entry, flows: entry.flows.map(f => ({ ...f, id: remap(f.id) })) };
      } else {
        updatedTrash[k] = entry;
      }
    }

    set({ flows: updatedFlows, activeFlowId: newActiveFlowId, _implFlowTrash: updatedTrash });
  },

  // Remap temp file / session ids → backend UUIDs after createFile lands.
  // Mirrors updateFlowIds for files & sessions: rewrites every store
  // surface that holds the old id (file row, session row, activeFileId,
  // and any flow whose sessionId pointed at the temp id). Without this,
  // saveProjectState would still send the temp session id to the
  // backend (where flowStateToBackend's UUID guard drops it), leaving
  // freshly-saved flows with session_id=NULL and orphaning them from
  // the file in the assembled-code view.
  updateFileIds: (fileMappings, sessionMappings) => {
    const { files, sessions, flows, globalResources, activeFileId } = get();
    const fileIdMap = new Map((fileMappings || []).map((m) => [m.oldId, m.newId]));
    const sessionIdMap = new Map((sessionMappings || []).map((m) => [m.oldId, m.newId]));

    const remappedFiles = files.map((f) =>
      fileIdMap.has(f.id) ? { ...f, id: fileIdMap.get(f.id) } : f
    );
    const remappedSessions = (sessions || []).map((s) => {
      let next = s;
      if (sessionIdMap.has(s.id)) next = { ...next, id: sessionIdMap.get(s.id) };
      if (fileIdMap.has(next.fileId)) next = { ...next, fileId: fileIdMap.get(next.fileId) };
      return next;
    });
    const remappedFlows = flows.map((f) =>
      sessionIdMap.has(f.sessionId) ? { ...f, sessionId: sessionIdMap.get(f.sessionId) } : f
    );
    // ADO #446 — class fields (location:'classField') are scoped by sessionId +
    // fileId, and those temp ids get remapped on first save too. Without
    // remapping the resources here, a class variable's sessionId no longer
    // matches its class session, so it stops emitting (and per-class attributes
    // break).
    const remappedGlobals = (globalResources || []).map((r) => {
      let next = r;
      if (r && r.sessionId && sessionIdMap.has(r.sessionId)) next = { ...next, sessionId: sessionIdMap.get(r.sessionId) };
      if (next && next.fileId && fileIdMap.has(next.fileId)) next = { ...next, fileId: fileIdMap.get(next.fileId) };
      return next;
    });

    set({
      files: remappedFiles,
      sessions: remappedSessions,
      flows: remappedFlows,
      globalResources: remappedGlobals,
      activeFileId: fileIdMap.has(activeFileId) ? fileIdMap.get(activeFileId) : activeFileId,
    });
  },

  // Reset to initial state (new project)
  resetProject: () => {
    const newFlow = createInitialFlow();
    set({
      projectName: 'My Program',
      projectId: null,
      allTestsPassed: false,
      projectShared: false,
      contractPinned: false,
      flows: [newFlow],
      activeFlowId: newFlow.id,
      files: [],
      activeFileId: null,
      globalResources: [],
      globalHistory: [JSON.stringify([])],
      globalHistoryIndex: 0,
      hasUnsavedChanges: false,
      // ADO #274 — fresh project resets all dirty state.
      dirtyFlowIds: new Set(),
      projectMetaDirty: false,
      lastSavedAt: null,
      isSaving: false,
      isLoading: false,
    });
  },
}));

// ADO #240 — expose the store on window for dev-console multi-session
// authoring while the proper outline UI for Add Nested Class / Add Sibling
// Class isn't built yet. Safe to leave on; it's a read+action handle, not
// a privileged API.
if (typeof window !== 'undefined') {
  window.useFlowStore = useFlowStore;
}

export default useFlowStore;
